// Miami, "Neon after rain on Ocean Drive". A warm June night on Ocean Drive, South Beach, minutes after a tropical
// shower has passed out to sea.
// Where we stand: on the Lummus Park side of Ocean Drive near 10th Street, a little above head height (about 6 m up:
// the upper deck of the park's beach pavilion), looking north by north-west up the street. The Art Deco hotel row
// (the west side of the street) runs away on the left in perspective: a low Mediterranean-revival cafe nearest, then
// the hero, an invented three-storey deco hotel with a stepped central tower and a neon fin, a streamline neighbour,
// a taller hotel whose blank south wall carries the city's billboard, and the pastel fronts beyond, each with its own
// neon. The wet street runs up the middle and mirrors it all. Right of the street: the park's coral-rock wall, lawn
// and palms, a royal poinciana in flower, the wavy promenade, the dunes' sea oats, the beach with a pastel lifeguard
// tower, and the Atlantic. The storm that left stands on the sea horizon and glows now and then with heat lightning;
// a waning moon comes out behind its drifting scud, and a cruise ship slides out to sea. (The park and the beach are
// compressed: the real lawn is about 45 m wide and the beach 100 m. The view is wide, from the hotel fronts in the
// west-north-west round to the sea in the east-north-east; a waning moon stands there only in the small hours.)
// Not visible from here: the downtown and Brickell skyline (behind the hotel row, across the bay; only its glow).
// Poinciana blossoms drift on the sea breeze at three depths (own kind 'poinciana'); the fronds and awnings still drip.
// render(t, { crowd }) thins the people; crowd 0 draws nobody (see CROWD).
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, FONT3, streak, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // the scene's 480x270 context, set in create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);
const R = (x, y, w, h, c) => { if (w > 0 && h > 0) { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); } };

/* ================= Layout: one street, one vanishing point ================= */
const HZ = 135, VPX = 330;                            // the sea horizon's row (eye level) and the street's vanishing column
const EYE = 6, FOC = 330;                             // eye height (m) and focal length (px)
const gs = y => (y - HZ) / EYE;                       // ground scale on row y (px per metre)
const gx = (L, y) => VPX - L * gs(y);                 // column of the ground point L metres left of our line (negative: right)
const rowAt = d => HZ + EYE * FOC / d;                // ground row d metres up the street
const HOTEL_L = 28, KERB_W = 17, KERB_E = 2, WALL_L = -2;       // laterals (m): hotel fronts, west kerb, east kerb, the park wall
const PROM_L = -6.5, DUNE_L = -9.5, SAND_L = -12.5, SEA_L = -28;  // the promenade, the dune, the sand, the waterline (compressed)
const S = x => (VPX - x) / HOTEL_L;                   // px per metre on the hotel fronts at column x
const baseY = x => HZ + EYE * S(x);                   // the fronts' foot at column x
const dOf = x => FOC * HOTEL_L / (VPX - x);           // how far up the street the front at column x is (m)
const xOf = d => VPX - FOC * HOTEL_L / d;
const HERO = { x0: 84, x1: 152, uc: 7.2 };            // the hero's front (columns) and its tower's axis (m along the front)
const HERO_D0 = dOf(HERO.x0);
const FIN_X = Math.round(xOf(HERO_D0 + HERO.uc));     // the fin's centre column
const MAST_TOP = 28;                                  // the row of the mast's tip (landmarkTop)
const BB = { x: 155, y: 76, w: 44, h: 20 };           // the billboard's face, on the tall hotel's blank south wall
const BB_LAMPS = [BB.x + 8, BB.x + 22, BB.x + 36];
const TALL_X = 202;                                   // the tall hotel's front corner
const PATH_Y = 206;                                   // the crosswalk and the park path to the beach (feet row)
const MOON = { x: 458, y: 44 };
const TOWER = { x: 447, y: 172 };                     // the lifeguard tower's foot on the sand (centre, base row)
const POIN = { x: 372, y: 187 };                      // the royal poinciana's foot in the park
const BENCH = { x: 381, y: 223 };
const LAMP_E = { x: Math.round(gx(KERB_E - 0.5, 198)), y: 198, top: 122 };   // the near lamp post on the park side

// The share of the people the scene shows by itself: 1 = everyone, about half by default. render(t, { crowd })
// overrides it. Rank 0 = the cast that stays at every level above 0 (the saxophonist, the waiter, the couple at
// their table, the one on the bench); crowd 0 = nobody, and what only they explain goes too (the saxophone and its
// notes, the tray, the glasses, the ridden bike, the pedicab, the dog).
const CROWD = 0.5;
const here = (rank, crowd) => crowd > 0 && rank < crowd;

/* ================= The loop's timeline (seconds) =================
   | what                                              | when                         | every        |
   | the fin's tubes light tier by tier                | all loop                     | 8 s          |
   | the gecko darts at a moth under the cafe's tube   | 4, 34, 64 ...                | 30 s         |
   | the waiter carries a tray out and back            | 10, 50, 90 ...               | 40 s         |
   | the couple clink glasses                          | 22, 70, 118 ...              | 48 s         |
   | the saxophonist plays a phrase (notes rise)       | 6, 30, 54 ...                | 24 s         |
   | heat lightning glows inside the far storm         | 14 47 71.5 103 131 166 197 223 | 8 a loop   |
   | a low car cruises away up the street              | 20 .. 46                     | once         |
   | parakeets cross the sky                           | 28 .. 40                     | once         |
   | a letter of the fin's name stutters               | 38 .. 39.6, 158.4 .. 160     | twice        |
   | a jogger comes down the promenade (crowd > 0.6)   | 30 .. 96                     | once         |
   | the cruise ship slides out to sea (horn at 60)    | 52 .. 172                    | once (big)   |
   | the one on the bench films the ship               | 68 .. 84                     | once         |
   | the near lamp post stutters                       | 76.2 .. 77.6, 209 .. 210.1   | twice        |
   | a pedicab under its lights                        | 84 .. 120                    | once (big)   |
   | two friends and a small dog come up from the beach| 98 .. 144                    | once         |
   | the billboard's right lamp stutters               | 117.5 .. 119, 228.3 .. 229.4 | twice        |
   | a low car comes down the street                   | 138 .. 164                   | once         |
   | two gulls drift over the beach                    | 146 .. 172                   | once         |
   | a couple strolls away up the pavement (crowd > .65)| 148 .. 226                  | once         |
   | a skater rolls down the park pavement (crowd > .8)| 172 .. 208                   | once         |
   | a pelican glides over the surf                    | 184 .. 208                   | once (big)   |
   | a beach cruiser comes up the path, rings (212), rides away up the bike lane | 204 .. 236 | once |
   All loop long: clouds, the moon behind them, haze, swells every 4.8 s, palms, sea oats, the pennant, windows,
   candles, string lights, drips and their rings, ibises, a cat's tail, moths.
   Longest gap between the starts of two timed things: about 16 s (228 to 4 of the next loop, while the cruiser
   still rides away until 236); the fin's 8 s light cycle runs through it. */
const HEAT = [14, 47, 71.5, 103, 131, 166, 197, 223]; // heat lightning (slow glows, 3 s each), the city's own weather only
const HEAT_LEN = 3;
const CAR_AWAY = { t0: 20, t1: 46 }, CAR_IN = { t0: 138, t1: 164 };
const PARAKEETS = { t0: 28, t1: 40 };
const STUTTER = [[38, 39.6], [158.4, 160]];
const SHIP = { t0: 52, t1: 172, horn: 60 };
const PEDICAB = { t0: 84, t1: 120 };
const FRIENDS = { t0: 98, t1: 144 };
const SKATER = { t0: 172, t1: 200 };
const PELICAN = { t0: 184, t1: 208 };
const CRUISER = { t0: 204, t1: 236, turn: 215, bell: 212 };
const LAMP_FLICK = [[76.2, 77.6], [209, 210.1]];      // the near lamp post stutters
const BB_FLICK = [[117.5, 119], [228.3, 229.4]];      // the billboard's right lamp stutters

/* ================= Small pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 */
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** posterised pick: flat bands with a narrow dither seam (plaster, paving) */
function pickFlat(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * 0.3) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** crop a pix buffer to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = o => ctx.drawImage(o.c, o.x, o.y);
const MIXED = {};
function mixHex(a, b, t) {                            // #rrggbb between two colours (cached)
  const key = a + b + t;
  if (MIXED[key]) return MIXED[key];
  const A = C(a), B = C(b), h = k => Math.round(lerp(A[k], B[k], t)).toString(16).padStart(2, '0');
  return (MIXED[key] = '#' + h(0) + h(1) + h(2));
}
function line(p, x0, y0, x1, y1, col) {               // 1 px line into a pix buffer
  const n = Math.max(1, Math.round(Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0))));
  for (let k = 0; k <= n; k++) p.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
}
function textP(p, s, x, y, col, sc = 1) {             // FONT3 text into a pix buffer at scale sc
  let cx = x;
  for (const ch of s) { const g = FONT3[ch]; if (g) g.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') p.fill(cx + i * sc, y + j * sc, sc, sc, col); }); cx += 4 * sc; }
}
function sprite(rows, pal, flip = false) {            // a small sprite from rows of palette keys ('.' = clear)
  const w = rows[0].length, p = pix(w, rows.length);
  rows.forEach((row, j) => { for (let i = 0; i < w; i++) if (row[i] !== '.') p.set(flip ? w - 1 - i : i, j, pal[row[i]]); });
  return p.done();
}
/** flattened dithered ellipse of light (pools on the wet ground), drawn additively */
function makePool(rx, ry, col, maxA) {
  const w = rx * 2 + 1, h = ry * 2 + 1, p = pix(w, h), c = hex(col);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const d = Math.hypot((x - rx) / rx, (y - ry) / ry);
    if (d >= 1) continue;
    const q = Math.floor(Math.pow(1 - d, 1.5) * 6 + bayer(x, y)) / 6;
    if (q > 0) p.set(x, y, c, Math.round(q * maxA * 255));
  }
  return { c: p.done(), rx, ry };
}
function drawPool(pl, x, y, a) {
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = a;
  ctx.drawImage(pl.c, Math.round(x) - pl.rx, Math.round(y) - pl.ry);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}
function flick(t, list, id) {                         // a lamp's stutter inside its windows, 1 otherwise
  for (const [a, b] of list) if (t >= a && t < b) { const hv = hash(id, Math.floor(t * 20)); return hv > 0.45 ? 1 : hv > 0.2 ? 0.45 : 0.12; }
  return 1;
}

/* ================= Sky: a humid tropical night, the city's rose glow low down, darker out to sea ================= */
const SKY = ['#050a22', '#07102c', '#0a1636', '#0e1c40', '#13224a', '#1a2853', '#222d5b', '#2c3162', '#383567', '#45396b',
  '#533d6e', '#62426f', '#724870', '#825070', '#915a71', '#9f6672', '#ab7374', '#b58178'];
function renderSky() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 150), 1.3) * 0.72;
    v += Math.max(0, 1 - Math.hypot((i - 120) / 280, (y - 142) / 105)) * 0.3;        // downtown's glow over the roofs
    v += Math.max(0, 1 - Math.hypot((i - VPX) / 130, (y - HZ) / 46)) * 0.14;         // the lit north end of the beach
    v -= smooth(350, 480, i) * 0.2 * Math.min(1, y / HZ);                            // darker out to sea
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 80, (y - MOON.y) / 55)) * 0.07;
    p.set(i, y, pick(SKY, v, i, y));
  }
  return p.done();
}
const STARS = [];
{ const r = rng(12); while (STARS.length < 46) { const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 96); if (Math.hypot(x - MOON.x, y - MOON.y) < 22) continue; STARS.push({ x, y, k: 20 + Math.floor(r() * 50), ph: r(), b: (0.3 + r() * 0.5) * (1 - y / 130) }); } }
/** a waning gibbous moon: the dark limb to the upper right, hardly lighter than the sky */
function renderMoon() {
  const r = 7, s = 17, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - 8, dy = y - 8, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    if (Math.hypot(dx - 7.5, dy + 5) < r - 0.2) { p.set(i, y, '#1c2452'); continue; }
    let col = dd > r - 1 ? '#d6cba6' : dx + dy > 3 ? '#e8dfbc' : '#f8f2d8';
    if (Math.hypot(dx + 2, dy - 2) < 1.6 || Math.hypot(dx - 1, dy - 4) < 1.2 || Math.hypot(dx + 3, dy + 2) < 1.1) col = '#cfc4a2';
    p.set(i, y, col);
  }
  return p.done();
}

/* ================= Clouds: the storm's scud, drifting out to sea (tileable, moonlit tops, city-lit bellies) ================= */
const CLOUD_DEFS = [
  { seed: 13, y: -6, h: 58, banks: 5, k: 1, pal: { top: '#3a467c', body: '#242b58', core: '#1c224a', bot: '#4c3a6c', bot2: '#34305e' } },
  { seed: 27, y: 28, h: 64, banks: 5, k: 2, pal: { top: '#56609c', body: '#333b70', core: '#2a3162', bot: '#80588c', bot2: '#544478' } },
  { seed: 41, y: 72, h: 56, banks: 4, k: 3, pal: { top: '#6c70ac', body: '#3d3f74', core: '#323466', bot: '#b0708e', bot2: '#74527e' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = (b + r() * 0.8) * W / def.banks, bw = 50 + r() * 80, base = h * (0.5 + r() * 0.3);
    const np = 6 + Math.floor(r() * 6);
    for (let i = 0; i < np; i++) {
      const rx = 10 + r() * 18, ry = 4.5 + r() * 8;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.2, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const q of puffs) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const dy = y - q.y;
      let v = 1 - (dx * dx) / (q.rx * q.rx) - (dy * dy) / (q.ry * q.ry);
      if (y > q.base) v -= (y - q.base) * 0.4;
      if (v > best) best = v;
    }
    best += 0.16 * Math.sin(TAU * 11 * x / W + y * 0.45 + def.seed) * Math.sin(TAU * 5 * x / W - y * 0.27);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.set(x, y, col);
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= The storm that left: a cumulonimbus on the sea horizon, its anvil, a veil of rain ================= */
const STORM = { x: 362, y: 58 };                      // top-left of its canvas (118 x 78: down to the horizon)
const STORM_PUFFS = [[150, 22, 70, 9], [96, 26, 26, 8], [118, 40, 30, 14], [104, 56, 30, 14], [86, 66, 26, 12], [60, 70, 26, 9],
  [36, 73, 24, 7], [14, 75, 18, 5], [112, 70, 24, 12], [76, 48, 14, 9], [128, 30, 22, 10]];
const HEAT_SPOTS = [[100, 46], [78, 62]];             // where the glow sits inside it (canvas coordinates)
function stormField(x, y) {
  let best = -9;
  for (const [cx, cy, rx, ry] of STORM_PUFFS) best = Math.max(best, 1 - ((x - cx) / rx) ** 2 - ((y - cy) / ry) ** 2);
  return best + 0.18 * Math.sin(x * 0.5 + y * 0.31) * Math.sin(x * 0.17 - y * 0.4);
}
function renderStorm() {
  const w = W - STORM.x, h = HZ - STORM.y, p = pix(w, h), lit = HEAT_SPOTS.map(() => pix(w, h));
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = stormField(x, y);
    if (v + th <= 0.03) {                             // under the cloud: a slanting veil of rain down to the sea
      if (y > 62 && x > 52 && stormField(x + (y - 62) * 0.35, 62) > 0.1 && bayer(x + (y >> 1), y) < 0.34) p.set(x, y, '#4a4474');
      continue;
    }
    let col = v > 0.6 + th ? '#161a3e' : '#1d2249';
    if (stormField(x, y - 1) + th <= 0.03 || stormField(x - 1, y - 1) <= 0) col = x > 70 ? '#7078b0' : '#4a528a';   // moonlit rims
    else if (stormField(x, y + 1) + th <= 0.03) col = '#5a4470';
    else if (stormField(x, y + 3) <= 0.1 + bayer(x, y) * 0.2) col = '#3a305e';
    p.set(x, y, col);
    HEAT_SPOTS.forEach(([hx, hy], k) => {
      const g = Math.pow(Math.max(0, 1 - Math.hypot((x - hx) / 46, (y - hy) / 30)), 1.4) * (0.55 + 0.45 * Math.min(1, v * 1.6));
      if (g > 0.06) lit[k].set(x, y, pick(['#2c2c60', '#4a4080', '#76609e', '#a888be', '#d8c0de'], g * 1.15, x, y));
    });
  }
  return { c: p.done(), lit: lit.map(q => q.done()) };
}
function heatAt(t) {                                  // { k: which spot, I: 0..1 }: one slow swell, a fainter one behind it
  for (let n = 0; n < HEAT.length; n++) {
    const s = t - HEAT[n];
    if (s < 0 || s >= HEAT_LEN) continue;
    const a = Math.sin(Math.PI * s / HEAT_LEN) ** 2, b = s > 1.3 ? Math.sin(Math.PI * (s - 1.3) / 1.7) ** 2 * 0.4 : 0;
    return { k: n % HEAT_SPOTS.length, I: Math.min(1, a * 0.8 + b) };
  }
  return null;
}

/* ================= The far end: the tall hotels north of 15th Street, in the haze ================= */
function renderFar() {
  const p = pix(W, H), r = rng(23), wins = [];
  for (const [x, w, top] of [[296, 9, 112], [306, 7, 104], [314, 8, 116], [323, 6, 108], [330, 9, 119], [340, 5, 124]]) {
    for (let y = top; y < HZ + 2; y++) for (let i = x; i < x + w; i++) p.set(i, y, y === top ? '#7c5c7e' : i === x ? '#6e5278' : '#644a72');
    for (let wy = top + 2; wy < HZ - 1; wy += 3) for (let wx = x + 1; wx < x + w - 1; wx += 2)
      if (r() < 0.4) wins.push({ id: wins.length, x: wx, y: wy, n: 3 + Math.floor(r() * 6), p: 0.5, off: r(), col: r() < 0.7 ? '#f0c890' : '#bcd4ee' });
  }
  return { c: trim(p), wins };
}

/* ================= The hotel row (static): fronts on the receding wall, painted column by column ================= */
// Every front lies on the plane HOTEL_L metres to our left: at column x a metre is S(x) px tall, so every horizontal
// (a parapet, an eyebrow, a stripe) runs to the vanishing point. u = metres along the front, v = metres above its foot.
let HOT, LITP, NEONP;                                 // pix buffers at build time: the row, its switching windows lit, its tubes lit
const WINS = new Map();                               // windows that switch on and off: key -> bounding box and rhythm
const TUBES = {};                                     // neon elements: name -> { x0, y0, x1, y1, pts, col }
const AQUA = '#4ff0e0', PINK = '#ff5cb0', YEL = '#ffd84a', LILAC = '#b890ff', MINT = '#7dffb0';
const WLIT = ['#ffd890', '#ffbe6e', '#ffe6b4', '#9ad0ff', '#ffb4d4'];
const row = (v, v0, s) => Math.abs(v - v0) * s < 0.5; // the one row nearest height v0
function glass(key, i, y, top) {                      // one pane pixel of window `key`: baked dark or lit, or switching
  const h = hash(key, 7, 3), litCol = WLIT[Math.floor(hash(key, 4, 3) * WLIT.length)], dark = top ? '#2c3868' : '#141a3a';
  if (h < 0.3) { HOT.set(i, y, dark); return; }
  if (h < 0.46) { HOT.set(i, y, top ? mixHex(litCol, '#ffffff', 0.4) : litCol); return; }
  HOT.set(i, y, dark); LITP.set(i, y, litCol);
  let w = WINS.get(key);
  if (!w) WINS.set(key, w = { id: key, x0: i, y0: y, x1: i, y1: y, n: 4 + Math.floor(hash(key, 1, 9) * 6), p: 0.3 + hash(key, 2, 9) * 0.4, off: hash(key, 3, 9) });
  w.x0 = Math.min(w.x0, i); w.x1 = Math.max(w.x1, i); w.y0 = Math.min(w.y0, y); w.y1 = Math.max(w.y1, y);
}
function tube(name, i, y, col) {                      // one pixel of neon element `name`: dim in the row, lit in NEONP
  i = Math.round(i); y = Math.round(y);
  HOT.set(i, y, mixHex(col, '#141430', 0.7)); NEONP.set(i, y, mixHex(col, '#ffffff', 0.4));
  let e = TUBES[name];
  if (!e) e = TUBES[name] = { name, x0: i, y0: y, x1: i, y1: y, pts: [], col };
  e.x0 = Math.min(e.x0, i); e.x1 = Math.max(e.x1, i); e.y0 = Math.min(e.y0, y); e.y1 = Math.max(e.y1, y); e.pts.push([i, y]);
}
function paintFront(B, pixel) {
  const d0 = dOf(B.x0);
  for (let i = Math.max(0, B.x0); i <= B.x1; i++) {
    const s = S(i + 0.5), by = baseY(i + 0.5), uL = dOf(i) - d0, uR = dOf(i + 1) - d0, u = (uL + uR) / 2;
    const top = B.top ? B.top(u) : B.hgt, y0 = Math.round(by - top * s);
    for (let y = y0; y <= Math.round(by); y++) {
      const c = pixel(B, u, (by - y) / s, i, y, s, uR - uL, y === y0);
      if (c) HOT.set(i, y, c);
    }
  }
}

/* ----- the hero: an invented three-storey deco hotel, the AZUMAR: stepped tower, neon fin, eyebrows, portholes ----- */
const WALL = ['#2a3058', '#424e7a', '#62759c', '#8a9fba', '#b0c4cc', '#d6e4de', '#f2f6ea'];        // white stucco at night, a mint cast
const WALLP = ['#342858', '#523676', '#784c92', '#a066aa', '#c888bc', '#e8b0d0', '#fcd8e6'];       // the same, washed pink by the fin
const GLOW = ['#5a2434', '#8a3a34', '#c0603a', '#f09448', '#ffc878', '#ffe8b4'];                   // lit lobbies and bars
const HERO_FW = dOf(HERO.x1 + 1) - HERO_D0;           // metres of front
const heroTop = u => { const a = Math.abs(u - HERO.uc); return a < 1.2 ? 17 : a < 2.2 ? 14.6 : a < 3.3 ? 13 : 11.6; };
const HERO_BAYS = [[0.15, 0.8], [1.15, 2.25], [2.65, 3.75], [10.65, 11.75], [12.15, 13.25], [13.6, 14.2]];
const FIN_FACE = ['#221a4a', '#2e2264', '#3e2a78'];
/** uplights in the marquee slab over the porch columns (not the door's): cones of light up the stucco, gone by the parapet */
const heroUp = (u, v) => {
  let g = 0;
  for (const cu of [0.25, 2.5, 4.75, 9.65, 11.9, 14.05]) g = Math.max(g, 1 - Math.abs(u - cu) / (0.35 + (v - 4.2) * 0.32));
  return g * Math.max(0, 1 - (v - 4.2) / 6.5) * 0.24;
};
function heroPixel(B, u, v, i, y, s, du, isTop) {
  const a = Math.abs(u - HERO.uc), tower = a < 3.3, fin = a < 1.2, edge = a0 => Math.abs(a - a0) < du / 2, finEdge = fin && a >= 1.2 - du;
  if (isTop) return fin ? '#fce6f2' : tower ? '#f0dcf0' : '#e6f4ec';                 // copings
  let lv = 0.88 - v / 36 + (v < 4.4 ? 0.02 : 0) - (u < 0.7 ? (0.7 - u) * 0.3 : 0) - (u > HERO_FW - 0.6 ? 0.1 : 0);
  if (v > 4.2) lv += heroUp(u, v);
  const ramp = clamp(1.3 - a / 3.4, 0, 1) * (v > 4.2 ? 1 : 0.35) > 0.5 + (bayer(i, y) - 0.5) * 0.4 ? WALLP : WALL;
  if (v > 11.6) {                                     // the stepped tower above the parapet: tubes up every edge
    if (finEdge) { tube(v < 14.6 ? 'h_fin' : 'h_t3', i, y, AQUA); return null; }
    for (const [a0, v0, v1, n] of [[3.3, 11.6, 13, 'h_t1'], [2.2, 13, 14.6, 'h_t2'], [1.2, 14.6, 17, 'h_t3']]) {
      if (edge(a0) && v >= v0 && v < v1) { tube(n, i, y, AQUA); return null; }
      if (a > a0 - 1.1 && a < a0 && row(v, v1 - 0.4, s) && a0 > 1.2) { tube(n, i, y, AQUA); return null; }
    }
    if (fin) return pickFlat(FIN_FACE, 0.5 + (u - HERO.uc) * 0.3, i, y);
    return pickFlat(WALLP, lv + 0.22 - (frac(u / 0.55) < 0.34 ? 0.14 : 0), i, y);      // fluted piers
  }
  if (!tower && v > 11) return row(v, 11.3, s) ? (tube('h_par', i, y, PINK), null) : pickFlat(ramp, lv + 0.1, i, y);
  if (v > 4.2) {
    if (fin) return finEdge && v > 6.7 ? (tube('h_fin', i, y, AQUA), null) : pickFlat(FIN_FACE, 0.5 + (u - HERO.uc) * 0.3, i, y);
    if (tower) {
      if (v > 4.5 && v < 6.5) return '#1c1c48';                                     // the sign panel (HOTEL)
      for (const pv of [8, 10.2]) if (Math.hypot(a - 2.25, (v - pv) * 0.9) < 0.52) { glass(4096 + (u < HERO.uc ? 200 : 216) + (pv > 9 ? 1 : 0), i, y, v > pv + 0.25); return null; }
      return pickFlat(WALLP, lv + 0.1 - (frac(u / 0.55) < 0.34 ? 0.14 : 0), i, y);
    }
    for (const bv of [10.55, 6.95]) {                                               // eyebrows and their shadow
      if (row(v, bv, s)) return '#f6fcf6';
      if (row(v + 1 / s, bv, s)) return '#1c2350';
    }
    if (row(v, 7.45, s) || row(v, 7.75, s) || row(v, 8.05, s)) return '#5ccab8';    // racing stripes
    const fl = v > 8.6 && v < 10.2 ? 1 : v > 5 && v < 6.6 ? 0 : -1;
    if (fl >= 0) for (let b = 0; b < HERO_BAYS.length; b++) if (u > HERO_BAYS[b][0] && u < HERO_BAYS[b][1]) {
      glass(4096 + b * 16 + fl, i, y, v > (fl ? 10 : 6.4));
      return null;
    }
    return pickFlat(ramp, lv, i, y);
  }
  if (v > 3.55) {                                     // the porch's marquee slab, a tube along its edge
    if (row(v, 3.75, s)) { tube('h_marq', i, y, AQUA); return null; }
    return v > 4.05 ? '#d8e8e2' : '#8aa0b8';
  }
  if (v < 0.45) return '#8c9cb4';                     // terrazzo step
  const door = u > 5.9 && u < 8.5;
  for (const cu of [0.25, 2.5, 4.75, 5.75, 8.65, 9.65, 11.9, 14.05]) if (Math.abs(u - cu) < 0.22) return pickFlat(ramp, lv + 0.2, i, y);   // porch columns
  if (row(v, 3.4, s) && i % 3 === 0) return '#fff2c0';                              // bulbs under the marquee
  if (!door && row(v, 1.35, s)) return '#d8e6e0';                                   // the porch rail
  if (!door && v < 1.35) return (i & 1) ? '#b8c8cc' : '#4a3a52';
  return pick(GLOW, (door ? 1 : 0.78) - v / 6 - (door ? Math.abs(u - 7.2) * 0.1 : 0.12 * Math.sin(u * 2.7) ** 2), i, y);
}
function paintHeroExtras() {
  const s = S(FIN_X), y0 = Math.round(baseY(FIN_X) - 16.3 * s), name = 'AZUMAR';     // the fin's name, letter by letter (2x)
  for (let k = 0; k < name.length; k++) FONT3[name[k]].forEach((r3, j) => {
    for (let c = 0; c < 3; c++) if (r3[c] === '1') for (let q = 0; q < 4; q++) {
      const i = FIN_X - 3 + c * 2 + (q & 1), vk = 16.3 - k * 12 / s;
      tube('h_L' + k, i, y0 + k * 12 + j * 2 + (q >> 1) + Math.round((vk - EYE) / HOTEL_L * (i - FIN_X)), PINK);
    }
  });
  'HOTEL'.split('').forEach((ch, k) => FONT3[ch].forEach((r3, j) => {               // HOTEL on the tower's sign panel
    for (let c = 0; c < 3; c++) if (r3[c] === '1') { const i = FIN_X - 9 + k * 4 + c; tube('h_hotel', i, Math.round(baseY(i) - 6.15 * S(i)) + j, AQUA); }
  }));
  const top = Math.round(baseY(FIN_X) - 17 * s);                                    // the mast and its ball
  for (let y = MAST_TOP + 2; y < top; y++) HOT.set(FIN_X, y, y % 6 < 2 ? '#c8d4e6' : '#7c88ac');
  HOT.fill(FIN_X - 1, MAST_TOP, 3, 2, '#ffe8f4');
}
function paintHeroSide() {                            // its south wall over the cafe's roof: a flat face, square to us
  const s = S(HERO.x0), by = baseY(HERO.x0), top = Math.round(by - 11.6 * s);
  for (let i = 5; i < HERO.x0; i++) for (let y = top; y <= 124; y++) {
    const v = (by - y) / s, m = (HERO.x0 - i) / s, q = frac((m + 0.5) / 2.2);       // m: metres back from the corner
    const lv = 0.47 - m * 0.016 + (m < 0.9 ? 0.12 : 0);
    if (y === top) HOT.set(i, y, '#cadcdc');
    else if (row(v, 11.3, s)) tube('h_par', i, y, PINK);
    else if (row(v, 10.55, s)) HOT.set(i, y, '#dceae4');
    else if (row(v + 1 / s, 10.55, s)) HOT.set(i, y, '#1c2350');
    else if (v > 8.6 && v < 10.2 && q > 0.3 && q < 0.86) glass(8192 + Math.floor((m + 0.5) / 2.2) * 16, i, y, v > 10);
    else if (row(v, 7.45, s) || row(v, 7.75, s) || row(v, 8.05, s)) HOT.set(i, y, '#4aaea2');
    else HOT.set(i, y, pickFlat(WALL, lv, i, y));
  }
}

/* ----- the neighbours: one painter, a style each ----- */
const PEACH = ['#3e2a4e', '#6c4660', '#9c6670', '#c88c7e', '#ecb894'], WHITE = ['#2a3260', '#44567e', '#6884a2', '#94b0bc', '#c8e0d6'];
const LIL = ['#2c2a5c', '#463c78', '#685a96', '#9080b4', '#bcacd4'], PINKP = ['#3c2658', '#62386e', '#8e5288', '#bc72a0', '#e89ec0'];
const MINTP = ['#1e3452', '#2c546c', '#427c84', '#62a69a', '#94d2b6'], CREAM = ['#3a3452', '#5e5262', '#8a7c72', '#bcaa80', '#eadc9e'];
const BLUE = ['#203062', '#304c7e', '#48709e', '#6c98b8', '#9cc4d6'];
const ROW = [
  { id: 'med', k: 10, x0: -40, x1: 81, hgt: 8.4, floors: 1, fh: 3.4, bay: 3.1, pal: PEACH, tile: true, arch: true, awn: ['#c8463c', '#f0e6d0'], balc: true },
  { id: 'b3', k: 3, x0: 153, x1: 200, hgt: 11, floors: 2, bay: 3.3, pal: WHITE, ribbon: true, brow: true, stripe: '#3fae9c', neon: MINT },
  { id: 'b4', k: 4, x0: TALL_X, x1: 232, hgt: 21, floors: 6, fh: 2.75, bay: 3.2, pal: LIL, neon: LILAC },
  { id: 'b5', k: 5, x0: 233, x1: 256, hgt: 10.5, floors: 2, bay: 4, pal: PINKP, brow: true, neon: YEL },
  { id: 'b6', k: 6, x0: 257, x1: 276, hgt: 13, floors: 3, fh: 2.8, bay: 5, pal: MINTP, neon: AQUA },
  { id: 'b7', k: 7, x0: 277, x1: 292, hgt: 9.5, floors: 2, bay: 6, pal: CREAM, neon: PINK },
  { id: 'b8', k: 8, x0: 293, x1: 306, hgt: 15, floors: 4, fh: 2.6, bay: 8, pal: BLUE, neon: LILAC },
  { id: 'b9', k: 9, x0: 307, x1: 319, hgt: 11, floors: 2, bay: 12, pal: PINKP, neon: MINT },
];
function frontPixel(B, u, v, i, y, s, du, isTop) {
  const P = B.pal, fh = B.fh || 3.3, wide = B.bay / du >= 4, q = frac(u / B.bay), bi = Math.floor(u / B.bay);
  if (isTop) return P[4];
  if (B.tile && v > B.hgt - 0.9) return ((i + (v > B.hgt - 0.45 ? 1 : 0)) & 1) ? '#b85a3a' : '#7e3626';   // barrel-tile eave
  if (B.neon && row(v, B.hgt - 0.55, s)) { tube(B.id + '_par', i, y, B.neon); return null; }
  let lv = 0.54 - v / (B.hgt * 2.4) + (v < 4.4 ? 0.2 : 0) - (u < 0.5 ? 0.12 : 0);
  if (B.stripe && (row(v, B.hgt - 1.5, s) || row(v, B.hgt - 1.85, s) || row(v, B.hgt - 2.2, s))) return B.stripe;
  if (v < 3.6) {                                      // the ground floor: a lit front behind piers (arches on the cafe)
    if (v < 0.4) return P[2];
    const pier = wide ? (q < 0.13 || (B.arch && v > 2.3 && Math.abs(q - 0.565) > 0.435 - (v - 2.3) * 0.25)) : (i % 3 === 0);
    return pier ? pickFlat(P, lv + 0.15, i, y) : pick(GLOW, 0.86 - v / 5.5 - 0.12 * Math.sin(u * 2.3 + B.k) ** 2, i, y);
  }
  if (v < 4.3) {                                      // an awning's valance, or a marquee slab with a tube
    if (B.awn) return B.awn[Math.floor(u / 0.8) & 1];
    if (B.neon && row(v, 3.8, s)) { tube(B.id + '_marq', i, y, B.neon); return null; }
    return v > 4.1 ? P[4] : P[1];
  }
  const fi = Math.floor((v - 4.3) / fh), fv = v - 4.3 - fi * fh;
  if (fi < B.floors) {
    if (B.brow && row(fv, 2.6, s)) return P[4];
    if (B.brow && row(fv + 1 / s, 2.6, s)) return P[0];
    if (B.balc && row(fv, 0.5, s) && q > 0.2 && q < 0.92) return '#1a1830';                               // an iron balcony
    if (fv > 0.8 && fv < 2.3) {
      if (!wide) { const h = hash(i, fi, B.k); return h < 0.3 ? WLIT[Math.floor(h * 16)] : h < 0.62 ? '#141a3a' : pickFlat(P, lv, i, y); }
      const a = B.ribbon ? 0.06 : 0.26, b = B.ribbon ? 0.94 : 0.78;
      const arch = B.arch && fv > 1.9 ? (fv - 1.9) * 0.45 : 0;
      if (q > a + arch && q < b - arch && !(B.ribbon && Math.abs(q - 0.5) < 0.04)) { glass(B.k * 4096 + bi * 16 + fi, i, y, fv > 2.1); return null; }
    }
  }
  return pickFlat(P, lv, i, y);
}
function paintTallSide() {                            // the tall hotel's blank south wall (square to us), over the streamline roof
  const s = S(TALL_X), by = baseY(TALL_X), top = Math.round(by - 21 * s);
  for (let i = 152; i < TALL_X; i++) for (let y = top; y < 116; y++) {
    const m = (TALL_X - i) / s, v = (by - y) / s, q = frac(m / 3.4), fv = frac((v - 4.3) / 2.75) * 2.75;
    if (y === top) HOT.set(i, y, '#8a7cb0');
    else if (q > 0.36 && q < 0.6 && fv > 0.9 && fv < 2.1 && m > 1) glass(45056 + Math.floor(m / 3.4) * 16 + Math.floor((v - 4.3) / 2.75), i, y, fv > 1.9);
    else HOT.set(i, y, pickFlat(LIL, 0.3 - m * 0.012 + (TALL_X - i < 2 ? 0.12 : 0), i, y));
  }
}
function paintTallSign() {                            // HOTEL down the tall hotel's corner, in lilac tubes
  const x = TALL_X + 2;
  'HOTEL'.split('').forEach((ch, k) => FONT3[ch].forEach((r3, j) => { for (let c = 0; c < 3; c++) if (r3[c] === '1') tube('b4_sign', x + c, 74 + k * 7 + j + Math.round(c * 0.2), LILAC); }));
}
function paintCafeSign() {                            // CAFE in yellow tubes over the cafe's awning
  'CAFE'.split('').forEach((ch, k) => FONT3[ch].forEach((r3, j) => {
    for (let c = 0; c < 3; c++) if (r3[c] === '1') for (let q = 0; q < 4; q++) {
      const i = 16 + k * 9 + c * 2 + (q & 1);
      tube('med_cafe', i, Math.round(baseY(i) - 7.45 * S(i)) + j * 2 + (q >> 1), YEL);
    }
  }));
}
let HOT_C, LIT_C, NEON_C, WINLIST, TUBELIST;
function renderHotels() {
  HOT = pix(W, H); LITP = pix(W, H); NEONP = pix(W, H);
  for (let k = ROW.length - 1; k >= 3; k--) paintFront(ROW[k], frontPixel);
  paintFront(ROW[2], frontPixel); paintTallSide(); paintTallSign();
  paintFront(ROW[1], frontPixel);
  paintHeroSide();
  paintFront({ x0: HERO.x0, x1: HERO.x1, top: heroTop }, heroPixel); paintHeroExtras();
  paintFront(ROW[0], frontPixel); paintCafeSign();
  HOT_C = trim(HOT); LIT_C = LITP.done(); NEON_C = NEONP.done();
  WINLIST = [...WINS.values()].map(w => ({ ...w, bw: w.x1 - w.x0 + 1, bh: w.y1 - w.y0 + 1 }));
  TUBELIST = Object.values(TUBES).map((e, k) => {
    const pts = e.pts.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]), n = Math.max(1, Math.round(pts.length / 22)), glow = [];
    for (let j = 0; j < n; j++) glow.push(pts[Math.floor((j + 0.5) * pts.length / n)]);
    return { ...e, k, bw: e.x1 - e.x0 + 1, bh: e.y1 - e.y0 + 1, glow };
  });
  HOT = LITP = NEONP = null;
}
/** how bright a neon element burns at t: the fin's tiers light in turn, one letter of the name stutters twice a loop */
function tubeLevel(e, t) {
  const n = e.name;
  if (n === 'h_t1' || n === 'h_t2' || n === 'h_t3') {
    const x = frac(t / 8 - (+n[3] - 1) * 0.16);
    return 1 - 0.85 * smooth(0, 0.05, x) * (1 - smooth(0.13, 0.2, x));
  }
  if (n === 'h_L3') for (const [a, b] of STUTTER) if (t >= a && t < b) { const hv = hash(31, Math.floor(t * 18), 3); return hv > 0.5 ? 1 : hv > 0.25 ? 0.4 : 0; }
  if (n === 'b7_par') return 0.55 + 0.45 * smooth(-0.2, 0.2, wave(t, 20, 0.3));   // a far sign breathes
  return 1;
}
function drawHotels(t) {
  halo(HL.finGlow, FIN_X, 92, 0.9 + 0.1 * wave(t, 8)); halo(HL.finGlow, FIN_X, 150, 0.6);
  blit(HOT_C);
  for (const w of WINLIST) if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 5) < w.p) ctx.drawImage(LIT_C, w.x0, w.y0, w.bw, w.bh, w.x0, w.y0, w.bw, w.bh);
  for (const e of TUBELIST) {
    const lv = tubeLevel(e, t);
    if (lv > 0.03) { ctx.globalAlpha = lv; ctx.drawImage(NEON_C, e.x0, e.y0, e.bw, e.bh, e.x0, e.y0, e.bw, e.bh); ctx.globalAlpha = 1; }
    const hum = lv * (0.92 + 0.08 * wave(t, 1700 + e.k * 90, e.k * 0.37)), hl = HL.neon[e.col];
    for (const [x, y] of e.glow) halo(hl, x, y, hum);
  }
  // the pennant on the mast, in the sea breeze (it blows inland, to the left)
  for (let k = 1; k <= 7; k++) {
    const dy = Math.round(Math.sin(TAU * 45 * t / LOOP - k * 0.9) * k * 0.22);
    R(FIN_X - k, MAST_TOP + 3 + dy, 1, k < 5 ? 3 : 2, k % 3 === 0 ? '#ffe8f4' : '#ff7cc0');
  }
  halo(HL.finial, FIN_X, MAST_TOP + 1, 0.7 + 0.3 * wave(t, 60));
}

/* ================= The Atlantic: a dark sea under the leaving storm, low surf, the moon's path ================= */
const seaY = x => HZ + EYE * (x - VPX) / -SEA_L;      // the waterline's row at column x (right of the vanishing point)
function renderSea() {
  const p = pix(W, H);
  for (let x = 322; x < W; x++) for (let y = HZ; y < 184; y++) {
    const near = clamp((y - HZ) / Math.max(1, seaY(x) - HZ), 0, 1.2);
    const v = 0.12 + near * 0.5 + Math.max(0, 1 - Math.abs(x - MOON.x) / 26) * 0.22 + (hash(x >> 1, y, 5) - 0.5) * 0.16;
    p.set(x, y, y === HZ ? '#2a3a5c' : pick(['#0a1730', '#0d2440', '#113450', '#17485c', '#236a70'], v, x, y));
  }
  return trim(p);
}
const SWELLS = 5, SWELL_T = 24;                       // a swell line every 4.8 s, each takes 24 s to come in and run up
function drawSea(t) {
  const gsStep = step(t, 720);                        // the moon's path: glints that come and go
  ctx.fillStyle = '#f4ecc8';
  for (let y = HZ + 1; y < seaY(MOON.x); y++) for (let k = 0; k < 3; k++) {
    const hv = hash(y, k, gsStep);
    if (hv > 0.45) continue;
    ctx.globalAlpha = 0.25 + hv; ctx.fillRect(MOON.x - 9 - (y - HZ) / 3 + Math.floor(hash(y, k, gsStep + 9) * (18 + (y - HZ) * 0.7)), y, hv < 0.15 ? 2 : 1, 1);
  }
  for (let j = 0; j < SWELLS; j++) {
    const { cyc, u } = cycle(t, LOOP / SWELL_T, j / SWELLS);
    const big = 0.6 + 0.4 * hash(j, cyc, 81), foam = u > 0.8;
    const L = foam ? -(SEA_L + 1.5) - 4 * big * Math.sin(Math.PI * (u - 0.8) / 0.2) : lerp(96, -(SEA_L + 1.5), Math.pow(u / 0.8, 0.7));
    let x0 = 334, y0 = -1;
    for (let x = 334; x <= W; x++) {
      const y = x < W ? Math.round(HZ + EYE * (x - VPX) / L) : -9;
      if (y === y0) continue;
      if (y0 > HZ && hash(x0, cyc * 8 + j, 7) > (foam ? 0.12 : 0.34)) {
        if (foam) {
          const f = Math.sin(Math.PI * (u - 0.8) / 0.2);
          ctx.globalAlpha = 0.35 + 0.55 * f; R(x0, y0, x - x0, 1, '#eafcf6');
          ctx.globalAlpha = 0.4 * f; R(x0, y0 + 1, x - x0, 1, '#8cc0c4');
        } else {
          ctx.globalAlpha = smooth(0, 0.25, u) * (0.22 + 0.5 * u) * big; R(x0, y0, x - x0, 1, u > 0.62 ? '#c4ece6' : '#4a8e96');
          ctx.globalAlpha *= 0.7; R(x0, y0 + 1, x - x0, 1, '#07142a');
        }
      }
      x0 = x; y0 = y;
    }
  }
  ctx.globalAlpha = 1;
}
/* the cruise ship: a white wall of lit decks, far out, sliding north-east and fading into the haze */
function renderShip() {
  const p = pix(46, 11);
  for (let x = 0; x < 46; x++) for (let y = 4; y < 11; y++) {
    const bow = x > 41 ? (x - 41) : 0, stern = x < 2 ? 2 - x : 0;
    if (y - 4 < bow * 1.2 || (y > 8 && (bow > 2 || stern > 1))) continue;
    p.set(x, y, y >= 9 ? '#1a2244' : y === 4 ? '#cfd6ea' : (y & 1) ? ((x * 7 + y) % 5 ? '#ffe2a0' : '#8a94b4') : '#aab2cc');
  }
  for (let x = 8; x < 36; x++) { p.set(x, 3, (x % 3) ? '#ffe8b8' : '#9aa2c0'); if (x > 12 && x < 30) p.set(x, 2, '#c0c8de'); }
  p.fill(16, 0, 4, 2, '#d8deee'); p.fill(17, 0, 2, 1, '#ff7a8a'); p.fill(26, 1, 2, 1, '#aab2cc');   // funnel, radar
  p.set(44, 5, '#7dff9a'); p.set(1, 5, '#ffffff');
  return p.done();
}
function shipAt(t) {
  if (t < SHIP.t0 || t >= SHIP.t1) return null;
  const u = (t - SHIP.t0) / (SHIP.t1 - SHIP.t0);
  return { x: Math.round(lerp(W + 2, 374, 1 - (1 - u) ** 1.6)), a: Math.min(1, u * 12) * (1 - smooth(0.62, 1, u)) };
}
function drawShip(t) {
  const sh = shipAt(t);
  if (!sh) return;
  ctx.globalAlpha = sh.a; ctx.drawImage(SHIP_C, sh.x, HZ - 9);
  ctx.globalAlpha = sh.a * 0.3; ctx.drawImage(SHIP_C, 0, 3, 46, 6, sh.x + Math.round(wave(t, 90)), HZ + 2, 46, 3);   // its lights in the sea
  ctx.globalAlpha = 1;
  halo(HL.ship, sh.x + 22, HZ - 4, sh.a * (0.85 + 0.15 * wave(t, 40)));
}

/* ================= The ground (static): pink pavements, terrazzo terraces, wet asphalt, the park, the beach ================= */
const TERR = ['#2c2448', '#40305c', '#5a4070', '#785684', '#9a7494', '#c098a8'];
const PAVE = ['#2e2244', '#442e56', '#5e3e68', '#7c5278', '#9c6c8a'];                  // Miami Beach's pink concrete, wet, at night
const ASPH = ['#06071a', '#090b20', '#0c0f26', '#10142d', '#151a35'];
const LAWN = ['#0a1e20', '#0e2a28', '#143a30', '#1c4c3a', '#286246'];
const SAND = ['#3a3c60', '#4e4c70', '#646080', '#7c7690', '#948ca0', '#b0a6ae'];
const PUDDLES = [[60, 247, 34, 5], [152, 233, 26, 4], [122, 262, 30, 5], [196, 213, 14, 2], [236, 251, 22, 4], [96, 222, 16, 2], [268, 222, 12, 2], [228, 190, 9, 1], [318, 246, 16, 3], [282, 196, 8, 1]];
const inPuddle = (x, y) => PUDDLES.some(([cx, cy, rx, ry]) => ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 < 1);
const UPLIGHTS = [];                                   // [x, y] of the lamps in the grass under the palms (filled by the palms)
const promEdge = d => PROM_L + 0.6 * Math.sin(d / 6);
const BOLLARDS = [362, 381, 400];                 // low lights along the path across the lawn (columns)  // the promenade's wavy inland edge
function renderGround() {
  const p = pix(W, H);
  for (let y = HZ + 1; y < H; y++) for (let x = 0; x < W; x++) {
    const sc = gs(y), L = (VPX - x) / sc, d = FOC / sc, n = hash(x, y, 5) - 0.5;
    let col;
    if (L > HOTEL_L || L <= SEA_L) continue;
    if (L > KERB_W) {                                 // the hotels' side: terrace paving, then the pink pavement
      const lit = 0.34 + 0.3 * Math.exp(-(((d - 45) / 9) ** 2)) + 0.26 * Math.exp(-(((d - 31) / 6) ** 2)) + 0.2 * Math.exp(-(((d - 62) / 14) ** 2)) + (L - KERB_W) * 0.02;
      if (L < KERB_W + 0.3) col = '#b4a2c4';                                              // kerb stone
      else if (L > 21) col = pickFlat(TERR, lit + (((Math.floor(L / 0.9) + Math.floor(d / 0.9)) & 1) ? 0.1 : -0.06), x, y);
      else col = sc > 7 && frac(d / 3) < FOC / (EYE * sc * sc) / 3 ? PAVE[0] : pickFlat(PAVE, lit + n * 0.1, x, y);   // expansion joints
    } else if (L > KERB_E) {                          // wet asphalt
      col = pickFlat(ASPH, 0.4 + n * 0.12 - (y - 200) / 300, x, y);
      if (L > KERB_W - 0.25) col = '#07081a';                                             // the gutter under the kerb
      else if (Math.abs(Math.abs(L - 9.8) - 0.2) * sc < 0.5 && sc > 2) col = hash(x >> 1, y, 3) < 0.8 ? '#8a7a30' : '#5a5028';   // double yellow
      else if (L < 4.6 && L > 2.4) col = Math.abs(L - 4.5) * sc < 0.6 ? '#7a8a94' : pickFlat(['#0c2428', '#10322f', '#144038'], 0.5 + n * 0.7, x, y);   // the green bike lane
      else if (Math.abs(L - 14.7) * sc < 0.5 && frac(d / 5.5) < 0.6) col = '#6e6e92';     // parking bays
      else if (y >= PATH_Y - 4 && y <= PATH_Y + 4 && frac(L / 1.5) < 0.52 && L < 16.4 && L > 2.4) col = hash(x, y, 13) < 0.18 ? '#5c5a7c' : y === PATH_Y - 4 ? '#8482a4' : '#a2a0c0';   // the crosswalk
    } else if (L > WALL_L) {                          // the park-side pavement
      col = L > KERB_E - 0.3 ? '#a898b8' : pickFlat(PAVE, 0.36 + n * 0.1 + 0.3 * Math.exp(-(((y - LAMP_E.y - 8) / 22) ** 2)), x, y);
    } else {
      const path = y >= PATH_Y - 5 && y <= PATH_Y + 4, pe = promEdge(d);
      if (L > pe) {                                   // the coral-rock wall, the lawn, the path across it
        if (path) col = pickFlat(PAVE, 0.4 + n * 0.12, x, y);
        else if (L > WALL_L - 0.5) col = hash(x >> 1, y >> 1, 9) < 0.4 ? '#8e8698' : '#6c6680';
        else if (L > WALL_L - 0.75) col = '#1a1830';
        else {
          let lit = 0.3 + n * 0.5 + smooth(360, 480, x) * 0.12;
          for (const [ux, uy] of UPLIGHTS) lit += Math.max(0, 1 - Math.hypot((x - ux) / 26, (y - uy) / 8)) * 0.6;
          for (const bx of BOLLARDS) lit += Math.max(0, 1 - Math.hypot((x - bx) / 20, (y - PATH_Y + 8) / 7)) * 0.5;
          col = pick(LAWN, lit, x, y);
        }
      } else if (L > DUNE_L) {                        // the promenade: two-tone waves in the paving
        col = frac((L - pe) / 1.2 + 0.35 * Math.sin(d / 2.2)) < 0.5 ? '#8a6c8c' : '#b09aa8';
        if (L > pe - 0.25 || L < DUNE_L + 0.25) col = '#cbbcc4';
        col = mixHex(col, '#1a1c40', 0.3);
      } else if (L > SAND_L) col = path ? pick(SAND, 0.42 + n * 0.3, x, y) : hash(x >> 1, y, 21) < 0.45 ? pick(['#14302a', '#1e4436', '#2c5a42'], 0.5 + n, x, y) : pick(SAND, 0.3 + n * 0.3, x, y);   // the dune, the path cut through it
      else {
        const wet = smooth(SEA_L + 5, SEA_L, L);
        col = pick(SAND, 0.52 + n * 0.3 - wet * 0.4 + Math.max(0, 1 - Math.hypot((x - TOWER.x) / 40, (y - TOWER.y) / 12)) * 0.3 + 0.1 * Math.sin(L * 2.1 + d * 0.3), x, y);
        if (wet > 0.75) col = mixHex(col, '#2a4a6a', 0.45);
      }
    }
    if (L > WALL_L && inPuddle(x, y)) col = mixHex(col, '#060818', 0.6);
    p.set(x, y, col);
  }
  for (const [cx, cy, rx, ry] of PUDDLES) for (let a = 0; a < 80; a++) {                  // a light rim on the puddles' far side
    const th = a / 80 * TAU;
    if (Math.sin(th) < 0.25) p.set(cx + Math.cos(th) * (rx + 1), cy + Math.sin(th) * (ry + 1) - (Math.sin(th) < -0.5 ? 0 : 0), '#2c2a52');
  }
  for (const bx of BOLLARDS) { p.fill(bx, PATH_Y - 9, 1, 4, '#4a4668'); p.set(bx, PATH_Y - 9, '#fff0c0'); }
  const r = rng(77);                                  // fallen poinciana blossoms: on the pavements, the lawn and the street
  for (let k = 0; k < 150; k++) {
    const x = Math.floor(r() * W), y = 150 + Math.floor(r() * 118), L = (VPX - x) / gs(y);
    const road = L > KERB_E + 0.5 && L < KERB_W - 0.5, keep = r() < 0.25;           // the traffic leaves few on the road
    if (L > HOTEL_L - 3 || L < PROM_L || p.alpha(x, y) === 0 || (road && !keep)) continue;
    const c = r() < 0.5 ? '#e8452c' : r() < 0.5 ? '#ff7a3a' : '#b0301e';
    p.set(x, y, c); if (y > 205 && r() < 0.5) p.set(x + 1, y, c);
  }
  return trim(p);
}

/* ================= The wet street: everything above it mirrored about the fronts' foot, lamp and neon streaks ================= */
// The fronts' foot is a straight line on the frame, so the mirror image (squashed by MQ) is one affine map of the
// frame. It is drawn once into a scratch canvas and softened into a second one by a plain blit a pixel over (a sheared
// blit costs far more), which melts the facades' dither; the squash already blends the rows. It is laid on the street
// faintly, row by row with a slow ripple, and like any wet road it mirrors most at a grazing angle: far up the street,
// hardly at our feet, where the streaks under the neon and the lamps carry the light.
const MQ = 1.5, SNAP_H = 208;
let SNAP, SNAPX, MIR, MIRX, SOFT, SOFTX;
function snapMirror() {
  SNAPX.globalCompositeOperation = 'copy';
  SNAPX.drawImage(ctx.canvas, 0, 0, VPX, SNAP_H, 0, 0, VPX, SNAP_H);
  const b0 = HZ + EYE * VPX / HOTEL_L, sl = -EYE / HOTEL_L, m = 1 + 1 / MQ;             // foot(x) = b0 + sl x
  MIRX.globalCompositeOperation = 'copy';
  MIRX.setTransform(1, sl * m, 0, -1 / MQ, 0, b0 * m - HZ);                             // MIR's row 0 is the frame's row HZ
  MIRX.drawImage(SNAP, 0, 0);
  MIRX.setTransform(1, 0, 0, 1, 0, 0);
  SOFTX.globalCompositeOperation = 'copy'; SOFTX.drawImage(MIR, 0, 0);
  SOFTX.globalCompositeOperation = 'source-over'; SOFTX.globalAlpha = 0.5;
  SOFTX.drawImage(MIR, 1, 0);
  SOFTX.globalAlpha = 1;
}
function layMirror(t) {
  const a1 = TAU * 40 * t / LOOP, a2 = TAU * 90 * t / LOOP;
  for (let y = HZ + 3; y < H; y++) {
    const k = y - HZ, x0 = Math.max(0, Math.ceil(VPX - HOTEL_L * gs(y))), x1 = Math.floor(VPX - WALL_L * gs(y));
    const off = Math.round(Math.sin(a1 + k * 0.35) * (0.4 + k * 0.012) + Math.sin(a2 - k * 0.75) * 0.45);
    const xk = clamp(Math.ceil(VPX - KERB_W * gs(y)), x0, x1), fade = 1 - k / 260;     // the kerb: the pavement mirrors less than the road
    if (xk > x0) { ctx.globalAlpha = 0.16 * fade; ctx.drawImage(SOFT, x0, k, xk - x0, 1, x0 + off, y, xk - x0, 1); }
    ctx.globalAlpha = 0.3 * fade;
    if (x1 > xk) ctx.drawImage(SOFT, xk, k, x1 - xk, 1, xk + off, y, x1 - xk, 1);
  }
  ctx.globalAlpha = 0.28;                             // the puddles: a brighter, stiller mirror
  for (const [cx, cy, rx, ry] of PUDDLES) for (let y = cy - ry; y <= cy + ry; y++) {
    const hw = Math.floor(rx * Math.sqrt(Math.max(0, 1 - ((y - cy) / ry) ** 2)));
    if (hw > 0) ctx.drawImage(SOFT, cx - hw, y - HZ, hw * 2, 1, cx - hw + Math.round(Math.sin(a1 + y * 0.55) * 0.6), y, hw * 2, 1);
  }
  ctx.globalAlpha = 1;
}
// streaks under the brightest lights on the fronts: [column, width, colour, alpha, the element whose level it follows,
// the light's height on the front (m)]. Each starts at the light's mirror image (squashed by MQ, as the mirror is), so
// the fin's runs down the road through its own reflection; lights at the foot (0) start at the front's foot.
const STREAKS = [
  [FIN_X, 5, '#ff6ab8', 0.75, 'h_L2', 6], [FIN_X - 14, 5, '#ffc070', 0.3, null, 0], [FIN_X + 12, 4, '#5ff0e0', 0.4, 'h_marq', 3.75],
  [34, 8, '#ffd84a', 0.62, 'med_cafe', 4], [62, 6, '#ffb060', 0.28, null, 0], [176, 5, '#7dffb0', 0.4, 'b3_marq', 3.8], [TALL_X + 3, 4, '#b890ff', 0.46, 'b4_sign', 8],
  [218, 4, '#ffc070', 0.26, null, 0], [244, 3, '#ffd84a', 0.34, 'b5_marq', 3.8], [266, 3, '#4ff0e0', 0.32, 'b6_marq', 3.8], [284, 2, '#ff5cb0', 0.3, 'b7_marq', 3.8], [300, 2, '#b890ff', 0.26, null, 0],
].map(([x, w, col, a, el, v]) => ({ x, w, col, a, el, y0: Math.round(baseY(x) + v * S(x) / MQ) + 2 }));
const STREAK_LEN = 52;                                // core's streak() has faded out by then
const RINGS = [];
{ const r = rng(71); for (let i = 0; i < 22; i++) RINGS.push({ id: i, n: 40 + Math.floor(r() * 30), ph: r() }); }
function drawStreetFx(t, lamp) {
  for (const s of STREAKS) {
    const lv = s.el && TUBES[s.el] ? tubeLevel(TUBES[s.el], t) : 1;
    streak(ctx, t, s.x, s.w, s.col, s.a * lv, s.x, s.y0, Math.min(H, s.y0 + STREAK_LEN));
  }
  for (const l of LAMPS) if (l.h > 20) {              // and under the lamps' heads, from the foot of each post
    const a = l.near ? 0.5 * lamp : l.h > 40 ? 0.5 : 0.38, y0 = l.near ? l.y + 10 : l.y + 2;
    streak(ctx, t, l.hx, l.near ? 6 : l.h > 40 ? 4 : 2, '#ffe0a8', a, 91 + l.hx, y0, Math.min(H, y0 + STREAK_LEN));
  }
  // rings in the puddles and on the street: the last drops off the fronds, the wires and the awnings
  for (const g of RINGS) {
    const { cyc, u } = cycle(t, g.n, g.ph);
    if (u > 0.4) continue;
    const s = u / 0.4, q = PUDDLES[Math.floor(hash(g.id, cyc, 4) * PUDDLES.length)];
    const x = q[0] + (hash(g.id, cyc, 1) - 0.5) * q[2] * 1.5, y = q[1] + (hash(g.id, cyc, 2) - 0.5) * q[3] * 1.3;
    const rr = 1 + s * (2 + q[3] * 0.8), ry = rr * 0.34, pts = 6 + Math.floor(rr * 2.5);
    ctx.fillStyle = '#a8a4dc'; ctx.globalAlpha = (1 - s) * 0.6;
    for (let j = 0; j < pts; j++) { const a = j / pts * TAU; ctx.fillRect(Math.round(x + Math.cos(a) * rr), Math.round(y + Math.sin(a) * ry), 1, 1); }
  }
  ctx.globalAlpha = 1;
}

/* ================= Things that stand on the ground, by where they stand: at(L, d) -> column, row, scale ================= */
const at = (L, d) => { const s = FOC / d; return { x: VPX - L * s, y: HZ + EYE * s, s }; };
let DL = [];                                           // the frame's standing things and people, drawn far to near
const put = (y, f) => DL.push({ y, f });

/* ----- palms: coconut palms with a lean and royal palms, crowns pre-rendered in sway frames ----- */
const PALM_NF = 7;
const PALMS = [
  // the park: the row along the pavement (far to near), then two on the lawn
  { x: 333, y: 141, h: 10, rad: 3 }, { x: 335, y: 147, h: 21, rad: 6 }, { x: 339, y: 156, h: 36, rad: 10, lean: 2 },
  { x: 345, y: 170, h: 58, rad: 17, lean: -3 }, { x: 357, y: 197, h: 106, rad: 30, lean: 7, up: true },
  { x: 364, y: 176, h: 62, rad: 18, lean: 5 }, { x: 396, y: 212, h: 150, rad: 34, royal: true, up: true },
  // the hotels' pavement
  { x: 285, y: 150, h: 25, rad: 7 }, { x: 259, y: 158, h: 38, rad: 11, lean: -2 }, { x: 216, y: 172, h: 62, rad: 18, lean: 3, up: true },
];
PALMS.forEach((p, i) => { p.i = i; p.lean = p.lean || 0; p.k = 20 + (i * 7) % 13; p.k2 = 47 + (i * 11) % 17; p.ph = (i * 0.37) % 1; if (p.up && p.x > VPX) UPLIGHTS.push([p.x + 2, p.y + 2]); });
function renderCrown(pm, a) {
  const rad = pm.rad, ox = Math.ceil(rad * 1.15) + 3, oy = Math.ceil(rad * 0.8) + 2, p = pix(ox * 2 + 1, Math.ceil(rad * 2.1) + 6), r = rng(500 + pm.i);
  const angs = pm.royal ? [150, 185, 215, 245, 270, 295, 325, 355, 30, 200, 340] : rad < 8 ? [170, 215, 260, 300, 345, 20] : [166, 196, 226, 258, 290, 322, 352, 18, 138, 208, 334];
  const pts = [];
  angs.forEach((deg, fi) => {
    const th0 = (deg + (r() - 0.5) * 14) * Math.PI / 180, L = rad * (0.78 + r() * 0.36), ch = Math.cos(th0);
    const th = th0 + a * (0.09 + 0.06 * Math.abs(ch)) + a * 0.04 * fi / angs.length;
    const droop = (pm.royal ? 0.3 : 0.42) + 0.4 * Math.abs(ch) + a * 0.06 * Math.sign(ch);
    let lx = -99, ly = -99;
    for (let s = 0; s <= 1.0001; s += 0.5 / L) {
      const X = Math.round(ox + Math.cos(th) * L * s), Y = Math.round(oy + Math.sin(th) * L * s + droop * L * s * s);
      if (X === lx && Y === ly) continue;
      lx = X; ly = Y;
      pts.push([X, Y, 3]);
      if (s > 0.15) {
        const len = Math.round((1 + 2.4 * Math.sin(Math.PI * Math.min(1, s * 1.1))) * Math.max(0.6, rad / 15)), side = ch > 0 ? 1 : -1;
        for (let k = 1; k <= len; k++) pts.push([X + side * Math.floor(k / 3), Y + k, k === len ? 1 : 2]);
        if (s > 0.3) pts.push([X, Y - 1, 4]);
      }
    }
  });
  const COL = pm.up ? ['', '#2e7250', '#143a2e', '#216046', '#74c088'] : ['', '#1e4c44', '#0e2a2c', '#184240', '#3c7c70'];
  for (const [x, y, k] of pts) p.set(x, y, COL[k]);
  if (rad > 8) { p.fill(ox - 1, oy - 1, 3, 3, '#16302a'); if (!pm.royal) { p.set(ox - 1, oy + 2, '#5a4026'); p.set(ox + 1, oy + 2, '#74522e'); p.set(ox, oy + 3, '#5a4026'); } }
  return { c: p.done(), ox, oy };
}
function renderTrunk(pm) {
  const p = pix(W, H), w0 = pm.royal ? Math.max(2, pm.h / 24) : Math.max(1, pm.h / 22);
  for (let k = 0; k <= pm.h; k++) {
    const s = k / pm.h, x = pm.x + pm.lean * s * s, y = pm.y - k;
    const w = pm.royal ? w0 * (0.75 + 0.35 * Math.sin(Math.PI * Math.min(1, s * 1.5))) : w0 * (1.25 - 0.55 * s) + (s < 0.06 ? 1.5 : 0);
    const shaft = pm.royal && s > 0.84;               // the royal palm's green crownshaft
    for (let i = Math.round(x - w / 2); i <= Math.round(x + w / 2 - 0.5); i++) {
      const e = (i - (x - w / 2)) / Math.max(1, w), lv = (pm.up ? 0.72 - s * 0.9 : 0.3 - s * 0.2) + (e < 0.34 ? 0.16 : e > 0.7 ? -0.2 : 0);
      let col = shaft ? pick(['#143a2e', '#1e5a40', '#2e7a52'], 0.6 - e, i, y)
        : pm.royal ? pickFlat(['#2c2e4a', '#3e4260', '#565c78', '#747a92', '#9aa0b0'], lv + 0.1, i, y) : pick(['#241e30', '#3c3040', '#5e4a4a', '#8a6e58', '#b89a70'], lv, i, y);
      if (!pm.royal && pm.h > 30 && k % 3 === 0) col = '#1c1828';                         // leaf-scar rings
      if (pm.royal && !shaft && k % 7 === 0 && e > 0.2) col = '#3e4260';
      p.set(i, y, col);
    }
  }
  if (pm.up) { p.fill(pm.x + 3, pm.y, 3, 2, '#2a2a3a'); p.set(pm.x + 4, pm.y, '#fff0c0'); }   // the uplight in the grass
  return trim(p);
}
function drawPalm(pm, t) {
  const a = 0.7 * wave(t, pm.k, pm.ph) + 0.3 * wave(t, pm.k2, pm.ph * 2), cr = pm.crowns[Math.round((a + 1) / 2 * (PALM_NF - 1))];
  blit(pm.trunk);
  if (pm.up) halo(HL.uplight, pm.x + 4, pm.y - 2, 0.8);
  ctx.drawImage(cr.c, Math.round(pm.x + pm.lean) - cr.ox, pm.y - pm.h - cr.oy);
}

/* ----- the royal poinciana: a low trunk, a wide flat umbrella of feathery green, flame-red on top ----- */
function renderPoinciana() {
  const p = pix(W, H), cx = POIN.x, cy = 150, glints = [];
  for (const [x0, y0, x1, y1] of [[cx, POIN.y, cx - 1, 164], [cx - 1, 166, cx - 20, 155], [cx - 1, 165, cx + 18, 154], [cx - 1, 164, cx - 7, 151], [cx, 164, cx + 8, 150]])
    for (const o of [0, 1]) line(p, x0 + o, y0, x1 + o, y1, o ? '#2a1c26' : '#5a4240');
  for (let y = cy - 12; y <= cy + 13; y++) for (let x = cx - 34; x <= cx + 34; x++) {
    const dx = (x - cx) / 32, dy = (y - cy) / (y < cy ? 9 : 12), n = hash(x, y, 31);
    const v = 1 - dx * dx - dy * dy + (n - 0.5) * 0.5 + 0.12 * Math.sin(x * 0.7) * Math.sin(y * 0.9 + x * 0.2);
    if (v <= 0.02 || (v < 0.3 && n < 0.3)) continue;
    const topness = (cy + 3 - y) / 12 + (n - 0.5) * 0.5;                                // blossoms lie on top of the canopy
    if (topness > 0.1) {
      const L = 0.45 + topness * 0.4 - dx * 0.18 + (n - 0.5) * 0.4;
      p.set(x, y, pick(['#5a1420', '#8e2420', '#c83a22', '#f0602a', '#ff9444'], L, x, y));
      if (L > 0.7 && hash(x, y, 33) < 0.25) glints.push([x, y]);
    } else p.set(x, y, pick(['#0e2a24', '#16402e', '#22583a', '#347248'], 0.3 + (y - cy) / 26 + (n - 0.5) * 0.5, x, y));
  }
  p.fill(cx + 4, POIN.y, 3, 2, '#2a2a3a'); p.set(cx + 5, POIN.y, '#fff0c0');
  return { ...trim(p), glints };
}
function drawPoinciana(t) {
  const sw = Math.round(1.3 * wave(t, 26, 0.2) + 0.5 * wave(t, 61, 0.5)), P = POIN_C, cut = 158 - P.y;   // the canopy leans in the breeze
  ctx.drawImage(P.c, 0, cut, P.c.width, P.c.height - cut, P.x, P.y + cut, P.c.width, P.c.height - cut);
  ctx.drawImage(P.c, 0, 0, P.c.width, cut, P.x + sw, P.y, P.c.width, cut);
  halo(HL.uplight, POIN.x + 5, POIN.y - 2, 0.8); halo(HL.poin, POIN.x, 152, 0.8);
  const gsT = step(t, 960);
  ctx.fillStyle = '#ffd08a';
  P.glints.forEach(([x, y], i) => { if (hash(i, gsT, 31) < 0.06) ctx.fillRect(x + sw, y, 1, 1); });
}
UPLIGHTS.push([POIN.x + 5, POIN.y + 1]);

/* ----- the lifeguard tower: a pastel cabin on stilts, a ramp down the land side, a flag ----- */
function renderTower() {
  const p = pix(W, H), X = TOWER.x, Y = TOWER.y;
  for (const [dx, sp] of [[-8, -2], [8, 2], [-4, -1], [5, 1]]) for (let k = 0; k <= 10; k++) p.set(X + dx + sp * k / 10, Y - k, dx > 0 ? '#c8bca8' : '#8a8090');   // stilts
  line(p, X - 8, Y - 7, X + 8, Y - 3, '#7c7488'); line(p, X - 8, Y - 3, X + 8, Y - 7, '#7c7488');
  for (let k = 0; k <= 13; k++) { p.set(X - 10 - k, Y - 11 + Math.round(k * 0.8), '#d8cca8'); p.set(X - 10 - k, Y - 14 + Math.round(k * 0.8), '#e0c060'); if (k % 4 === 0) p.fill(X - 10 - k, Y - 13 + Math.round(k * 0.8), 1, 2, '#e0c060'); }   // the ramp and its rail
  p.fill(X - 11, Y - 12, 23, 2, '#d8cca8'); p.fill(X - 11, Y - 11, 23, 1, '#8a8090');   // the deck
  for (let i = X - 11; i <= X + 11; i += 2) p.set(i, Y - 14, '#e0c060');
  p.fill(X - 11, Y - 15, 23, 1, '#f0d470');
  for (let y = Y - 25; y < Y - 12; y++) for (let i = X - 7; i <= X + 7; i++)                // the cabin: pink boards, aqua trim
    p.set(i, y, i === X - 7 ? '#8e5a86' : i > X + 4 ? '#f0a8c4' : (y & 1) ? '#c87ca4' : '#d88cb0');
  p.fill(X - 4, Y - 22, 5, 5, '#22264a'); p.fill(X - 4, Y - 22, 5, 1, '#5cd0c4'); p.fill(X - 2, Y - 22, 1, 5, '#5cd0c4');   // shuttered window
  p.fill(X + 3, Y - 21, 3, 9, '#5cc0b8'); p.set(X + 5, Y - 17, '#f0d470');                 // the door
  for (let k = 0; k < 6; k++) p.fill(X - 11 + k, Y - 26 - k, 23 - k * 2 + (k < 2 ? 2 : 0), 1, k === 0 ? '#2e8a88' : k === 5 ? '#a8f0e0' : '#4cb8ae');   // the roof
  p.fill(X - 12, Y - 26, 2, 1, '#a8f0e0'); p.fill(X + 11, Y - 25, 2, 1, '#2e8a88');
  p.fill(X, Y - 37, 1, 6, '#c8c8d8');                                                     // the flag's pole
  return trim(p);
}
function drawTower(t) {
  blit(TOWER_C);
  for (let k = 0; k < 6; k++) R(TOWER.x - 1 - k, TOWER.y - 37 + Math.round(Math.sin(TAU * 50 * t / LOOP - k) * k * 0.2), 1, 3, k < 3 ? '#e8484a' : '#f0d470');   // the flag blows inland
}

/* ----- sea oats on the dune, nodding in the breeze ----- */
const OATS = [];
{ const r = rng(57); for (let i = 0; i < 46; i++) { const y = 146 + Math.floor(r() * 92), sc = gs(y), L = DUNE_L - 0.2 - r() * 2.6, x = Math.round(VPX - L * sc); if (x < W - 2 && Math.abs(y - PATH_Y) > 7) OATS.push({ x, y, h: Math.max(3, Math.round((1 + r() * 0.5) * sc)), k: 24 + Math.floor(r() * 18), ph: r() }); } }
OATS.sort((a, b) => a.y - b.y);
function drawOats(t) {
  for (const o of OATS) {
    const sw = wave(t, o.k, o.ph) * 0.8 + wave(t, 67, o.ph * 3) * 0.4, half = o.h >> 1;
    for (const dx of [-1, 1]) {
      const tip = Math.round((sw - 1) * o.h * 0.22);
      R(o.x + dx, o.y - half, 1, half, '#3e5a44');
      R(o.x + dx + Math.round(tip / 2), o.y - o.h + 1, 1, o.h - half, '#5c7850');
      R(o.x + dx + tip, o.y - o.h, 1, Math.max(1, o.h >> 2), '#c8b078');                  // the seed head
    }
  }
}

/* ----- street lamps: a pole, a bracket over the street, a warm head ----- */
const LAMPS = [{ x: LAMP_E.x, y: LAMP_E.y, h: LAMP_E.y - LAMP_E.top, dir: -1, near: true }, { x: 324, y: 160, h: 30, dir: -1 }, { x: 327, y: 148, h: 16, dir: -1 },
  { x: 206, y: 177, h: 50, dir: 1 }, { x: 250, y: 162, h: 33, dir: 1 }, { x: 290, y: 149, h: 16, dir: 1 }];
function renderLamp(l) {
  const p = pix(W, H), arm = Math.max(2, Math.round(l.h / 8)), wd = l.h > 60 ? 2 : 1;
  for (let k = 0; k < l.h; k++) for (let j = 0; j < wd; j++) p.set(l.x + j, l.y - k, j ? '#3c3c5a' : k % 9 === 0 ? '#6a6a8c' : '#565676');
  if (l.h > 40) { p.fill(l.x - 1, l.y - 4, wd + 2, 5, '#3a3a56'); p.fill(l.x - 1, l.y - 4, wd + 2, 1, '#6a6a8c'); }
  for (let k = 0; k <= arm; k++) p.set(l.x + l.dir * k, l.y - l.h - Math.round(Math.sin(k / arm * 1.5) * arm * 0.5), '#565676');
  l.hx = l.x + l.dir * arm; l.hy = l.y - l.h - Math.round(arm * 0.5) + 1;
  p.fill(l.hx - (l.h > 40 ? 1 : 0), l.hy, l.h > 40 ? 3 : 1, l.h > 60 ? 2 : 1, '#fff4d4');
  if (l.near) for (let y = l.y - 52; y < l.y - 34; y++) for (let i = 1; i <= 5; i++) p.set(l.x + wd - 1 + i, y, y % 6 < 2 ? '#f08cc0' : '#5cd0c4');   // a banner on the near post
  return trim(p);
}

/* ----- the cafe and hotel terraces (static): tables with candle lamps, chairs, umbrellas closed for the shower ----- */
const TABLES = [[23.5, 33, 2], [22.6, 29.6, 2], [24.3, 36.6, 0], [23.6, 41.5, 3], [23.8, 45.4, 0], [23.6, 49, 1], [23, 55, 0], [23.2, 60, 1], [23, 66, 0], [23, 75, 0], [23, 86, 0], [23, 100, 0]];
const CANDLES = [];                                    // [x, y] of each table's candle lamp
const STRING = [];                                     // the string lights' bulbs: [x, y]
const POLES = [[21.2, 27.6], [21.2, 33], [21.2, 38.8]].map(([L, d]) => at(L, d));
function renderTerrace() {
  const p = pix(W, H);
  for (const [L, d, kind] of TABLES) {
    const q = at(L, d), x = Math.round(q.x), y = Math.round(q.y), s = q.s, tw = Math.max(2, Math.round(0.85 * s)), ty = Math.round(y - 0.75 * s);
    if (s > 5.5 && kind < 2) for (const sd of [-1, 1]) {                               // empty chairs
      const cx = x + sd * Math.round(0.8 * s);
      p.fill(cx - 1, Math.round(y - 0.45 * s), 3, 1, '#3a3458'); p.fill(cx + (sd > 0 ? 1 : -1), Math.round(y - 0.95 * s), 1, Math.round(0.5 * s), '#4a4470');
      p.fill(cx - 1, Math.round(y - 0.45 * s) + 1, 1, Math.round(0.45 * s) - 1, '#2a2648'); p.fill(cx + 1, Math.round(y - 0.45 * s) + 1, 1, Math.round(0.45 * s) - 1, '#2a2648');
    }
    p.fill(x, ty, 1, y - ty + 1, '#2a2648');                                             // the table: a leg, a round top
    p.fill(x - (tw >> 1), ty, tw, 1, '#d8d0e4'); if (s > 6) p.fill(x - (tw >> 1), ty + 1, tw, 1, '#5a5480');
    CANDLES.push([x, ty - 1]);
    if (kind === 1 || kind === 3) {                                                      // a closed umbrella beside it
      const ux = x + Math.round(1.1 * s), uh = Math.round(2.5 * s);
      for (let k = 0; k < uh; k++) { const f = k / uh, w = f > 0.3 && f < 0.92 ? (s > 7 ? 3 : 2) : 1; for (let j = 0; j < w; j++) p.set(ux + j - (w >> 1), y - k, f > 0.3 && f < 0.92 ? (((k >> 1) + j) & 1 ? '#e87ab0' : '#f2e6ee') : '#4a4470'); }
    }
  }
  // string lights: from the cafe's wall out to three poles along the terrace's edge
  POLES.forEach(q => { const x = Math.round(q.x), y = Math.round(q.y), h = Math.round(3.1 * q.s); p.fill(x, y - h, 1, h + 1, '#3a3458'); q.tx = x; q.ty = y - h; });
  const run = [[0, Math.round(baseY(0) - 4.5 * S(0))], ...POLES.map(q => [q.tx, q.ty])];
  for (let k = 0; k + 1 < run.length; k++) {
    const [x0, y0] = run[k], [x1, y1] = run[k + 1], n = x1 - x0;
    for (let i = 0; i <= n; i++) {
      const f = i / n, y = Math.round(lerp(y0, y1, f) + Math.sin(Math.PI * f) * 3.2);
      p.set(x0 + i, y, '#241e3c');
      if (i % 5 === 2) STRING.push([x0 + i, y + 1]);
    }
  }
  return trim(p);
}
function drawTerrace(t) {
  blit(TERRACE_C);
  const sI = step(t, 480);
  STRING.forEach(([x, y], i) => {                                                        // warm bulbs, a slow twinkle
    const v = 0.72 + 0.28 * wave(t, 30 + (i * 7) % 23, i * 0.31);
    R(x, y, 1, 1, v > 0.8 ? '#fff0c4' : '#e8b060');
    if (i % 3 === 0) halo(HL.bulb, x, y, v * 0.8);
  });
  CANDLES.forEach(([x, y], i) => {                                                       // candle lamps: a small flame that wavers
    const f = 0.7 + 0.2 * wave(t, 420 + i * 31, i * 0.3) + 0.1 * hash(i, sI, 5);
    R(x, y, 1, 1, f > 0.85 ? '#fff4c8' : '#ffc060');
    halo(i < 6 ? HL.candle : HL.bulb, x, y, f);
  });
}

/* ----- parked cars: boxes built slice by slice up the street, far to near (the 1950s convertible is the near one) ----- */
// top(u, w) -> [height (m), colour of what you see from above]; side(u, v) and rear(w, v) -> colours of the street
// side and of the tail. u: metres from the tail, w: metres from the kerb side, v: metres above the road.
function renderCar(p, L0, d0, len, wid, top, side, rear) {
  for (let u = len; u >= 0; u -= 0.04) {
    const q = at(L0, d0 + u), s = q.s, n = Math.max(2, Math.round(wid * s));
    for (let k = 0; k <= n; k++) {
      const w = k / n * wid, x = Math.round(q.x + w * s), [h, tc] = top(u, w), y0 = Math.round(q.y - h * s), y1 = Math.round(q.y - 0.16 * s);
      for (let y = y0; y <= y1; y++) p.set(x, y, y === y0 ? tc : u < 0.05 ? rear(w, (q.y - y) / s) : side(u, (q.y - y) / s));
      p.set(x, Math.round(q.y), '#05060f');                                              // its shadow on the wet road
    }
  }
}
const TEAL = ['#1c6a78', '#2a9aa0', '#4cc8c0', '#8ae8dc'];
function renderConvertible(p) {
  const len = 5.4, wid = 1.9;
  const wheel = (u, v) => { for (const c of [1.0, 4.3]) { const rr = Math.hypot(u - c, v - 0.34); if (rr < 0.36) return rr < 0.12 ? '#d8dce8' : rr < 0.2 ? '#14121c' : rr < 0.28 ? '#e8e4e0' : '#14121c'; } return null; };
  const top = (u, w) => {
    const edge = Math.min(w, wid - w);
    if (u < 0.22 || u > len - 0.2) return [0.56, '#d8dce8'];                              // chrome bumpers
    if (u < 1.55) return edge < 0.2 ? [1.02 + 0.2 * (1 - u / 1.55), '#8ae8dc'] : [0.9, w > wid * 0.55 ? TEAL[2] : TEAL[1]];   // the trunk deck between two tail fins
    if (u < 1.95) return [0.98, '#f2ece0'];                                              // the folded top's boot
    if (u < 3.35) {                                                                      // the open cabin: benches in red and white
      if (edge < 0.18) return [0.93, TEAL[3]];
      if (u < 2.2 || (u > 2.7 && u < 2.95)) return [0.86, edge < 0.5 ? '#f2ece0' : '#d8404a'];
      return [0.56, u > 3.1 ? '#1a1a2c' : w > wid * 0.5 ? '#b8323c' : '#8c2630'];
    }
    if (u < 3.5) return [1.28, edge < 0.2 ? '#d8dce8' : '#7ab0c8'];                       // the windscreen in its chrome frame
    return [0.94 - (u - 3.5) * 0.03, w > wid * 0.6 ? TEAL[3] : w > wid * 0.25 ? TEAL[2] : TEAL[1]];   // the bonnet
  };
  const side = (u, v) => wheel(u, v) || (u > 3.35 && u < 3.5 && v > 0.95 ? '#9ac8dc' : v > 0.84 ? TEAL[2] : v > 0.52 && v < 0.6 ? '#d8dce8' : v < 0.3 ? '#12202c' : TEAL[1]);
  const rear = (w, v) => {
    const edge = Math.min(w, wid - w);
    if (v < 0.56) return v > 0.36 ? '#c8ccdc' : '#12141e';
    if (edge < 0.28 && v > 0.66 && v < 0.9) return '#ff4a4a';                             // tail lamps
    if (Math.abs(w - wid / 2) < 0.26 && v < 0.74) return '#f0e8c0';                       // the plate
    return edge < 0.2 ? TEAL[2] : v > 0.8 ? TEAL[1] : TEAL[0];
  };
  renderCar(p, 17, 30.6, len, wid, top, side, rear);
}
function renderSedan(p, d0, body, dark) {
  const top = (u, w) => (u < 0.9 || u > 3.5 ? [0.92, body] : [1.38, w > 0.9 ? mixHex(body, '#ffffff', 0.25) : body]);
  const side = (u, v) => (Math.hypot(u - 0.9, v - 0.3) < 0.32 || Math.hypot(u - 3.6, v - 0.3) < 0.32 ? '#101018' : v > 0.95 ? '#1c2240' : dark);
  const rear = (w, v) => (v > 0.95 ? '#2a3458' : (w < 0.3 || w > 1.5) && v > 0.6 && v < 0.8 ? '#c83040' : dark);
  renderCar(p, 17, d0, 4.6, 1.8, top, side, rear);
}
function renderParked() {                              // one canvas each, with the row of its tail: people pass before and behind them
  const out = [[118, '#5a5a7a', '#34344c'], [96, '#8a3a4a', '#4c2230'], [78, '#c8c8d8', '#6a6a84'], [63, '#2a4a7a', '#1a2c4c'], [52.5, '#d8b040', '#7a6228'], [42.5, '#3a3a52', '#22223a']]
    .map(([d0, body, dark]) => { const p = pix(W, H); renderSedan(p, d0, body, dark); return { ...trim(p), row: at(17, d0).y }; });
  const p = pix(W, H);
  renderConvertible(p);
  out.push({ ...trim(p), row: at(17, 30.6).y });
  return out;
}

/* ----- the bench under the palms, turned to the sea ----- */
function renderBench() {
  const p = pix(W, H), X = BENCH.x, Y = BENCH.y;
  p.fill(X - 6, Y - 13, 2, 8, '#d878a8'); p.fill(X - 6, Y - 13, 1, 8, '#8a4a78');          // the back rest, end on
  p.fill(X - 6, Y - 6, 13, 2, '#d878a8'); p.fill(X - 6, Y - 5, 13, 1, '#8a4a78');          // the seat
  p.fill(X - 5, Y - 4, 1, 5, '#3a3458'); p.fill(X + 5, Y - 4, 1, 5, '#3a3458');
  return trim(p);
}

/* ================= People: one function draws them all (a head, a body, legs, a colour each, lit by the scene) ================= */
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#d4d0cc'];
const NIGHT = '#16103a', NEONL = '#ffc4e4', MOONL = '#c4d4ff';
const TOPS = ['#e8505a', '#f0c848', '#f0ece2', '#3a7ad0', '#2ab09a', '#f08ab8', '#f0903a', '#58b058', '#9a6ad8', '#c8d4e8', '#ff6a8a', '#60c0e0'];
const BOTS = ['#33466e', '#22202c', '#b8a278', '#e0dcd4', '#3a3a58', '#4a3a30'];
/** a look: the proportions for a height of h px, and its colours dimmed for the night (dk), o.glow = the light on the shoulders */
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.22, m = c => mixHex(c, NIGHT, dk), gl = o.glow || NEONL;
  const hh = h >= 30 ? 6 : h >= 22 ? 5 : h >= 14 ? 4 : h >= 9 ? 3 : 2, th = Math.max(2, Math.round(h * 0.36));
  return { h, hh, hw: h >= 28 ? 5 : h >= 19 ? 4 : h >= 9 ? 3 : 2, th, tw: h >= 30 ? 9 : h >= 26 ? 8 : h >= 22 ? 7 : h >= 19 ? 6 : h >= 14 ? 5 : h >= 9 ? 4 : 3, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], gl, 0.3), top: m(top), topD: mixHex(top, NIGHT, dk + 0.3),
    topL: mixHex(top, gl, 0.4), bot: m(bot), shoe: m('#221a20'), long: h >= 12 ? (o.long || 0) : 0, skirt: o.skirt ? m(o.skirt) : null,
    hat: o.hat ? m(o.hat) : null, bag: o.bag ? m(o.bag) : null };
}
/** the same look at every height, for people who walk up or down the street: looks(...)[h] */
const looks = (...a) => { const L = []; for (let h = 4; h <= 36; h++) L[h] = look(h, ...a); return L; };
const walkF = t => Math.floor(t * 3.2) & 1;            // stride frame (3.2 a second: a whole number per loop)
/**
 * One person, feet on row fy, centred on column x. Returns the row of the top of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir: 1 right, -1 left); o.step: stride frame (0/1) when walking
 * o.sit: seated on an edge at row fy; o.lean: head px towards a neighbour
 * o.aL / o.aR: 0 down, 1 straight up, 2 bent up with a phone, 3 out, 6 hand to the mouth (profile)
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? Math.max(2, tw - 2) : tw, tx = x - (bw >> 1), legC = P.skirt ? P.skin : P.bot;
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.step != null && o.step) {
        R(x - 2, ly, 4, Math.min(2, legs), legC);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
      if (P.skirt) R(x - 2, ly, 4, (legs >> 1) + 1, P.skirt);
    } else {
      const lw = bw >= 7 ? 3 : bw >= 4 ? 2 : 1, up = o.step == null ? -1 : o.step;       // a stride lifts one foot a row
      R(tx, ly, bw, Math.min(2, legs), P.bot);
      R(tx, ly + 2, lw, legs - 2 - (up === 0 ? 1 : 0), legC); R(tx + bw - lw, ly + 2, lw, legs - 2 - (up === 1 ? 1 : 0), legC);
      if (legs > 3) { R(tx, fy - (up === 0 ? 1 : 0), lw, 1, P.shoe); R(tx + bw - lw, fy - (up === 1 ? 1 : 0), lw, 1, P.shoe); }
      if (P.skirt) R(tx, ly, bw, (legs >> 1) + 1, P.skirt);
    }
  } else if (side) {                                   // seated in profile: thighs forward, shins down
    const kl = Math.max(2, Math.round(P.h * 0.2)), kx = dir > 0 ? x : x - kl;
    R(kx, fy - 1, kl + 1, 2, legC); R(dir > 0 ? x + kl - 1 : x - kl, fy + 1, 2, Math.round(P.h * 0.22), legC);
    R(dir > 0 ? x + kl - 1 : x - kl - 1, fy + 1 + Math.round(P.h * 0.22), 3, 1, P.shoe);
    if (P.skirt) R(kx, fy - 1, kl + 1, 2, P.skirt);
  } else R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  R(tx, ty, bw, 1, P.topL);                                                     // the scene's light on the shoulders
  if (P.bag) { if (view === 0) R(tx + 1, ty + 1, bw - 2, th - 2, P.bag); else if (side) R(dir > 0 ? tx - 1 : tx + bw - 1, ty + 1, 2, th - 2, P.bag); }
  if (side) {
    const st = o.aR || 0;
    if (st === 0) { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 1) { R(x + dir, hy - 1, 1, ty - hy + 2, P.topD); R(x + dir, hy - 3, 1, 2, P.skin); }
    else if (st === 2) { for (let k = 0; k < 4; k++) R(x + dir * (1 + k), ty + 1 - k, 1, 1, k < 3 ? P.topD : P.skin); R(x + dir * 5 - (dir < 0 ? 1 : 0), ty - 5, 2, 3, '#0e1018'); R(x + dir * 5 - (dir > 0 ? 0 : 1) - dir, ty - 4, 1, 1, '#9ad8ff'); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); }
  } else {
    const arm = (ax, st, sg) => {
      if (!st) { R(ax, ty + 1, 1, Math.max(1, th - 2), P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
      else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
      else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin); const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); if (view === 0) { R(p0, hy - 2, 2, 1, '#ffe2a8'); R(p0, hy - 1, 2, 1, '#62d8c8'); } }
      else { R(sg < 0 ? ax - 2 : ax, ty + 1, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 1, 1, 1, P.skin); }
    };
    if (bw >= 4) { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else if (hw > 2) R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); if (side) R(dir > 0 ? hx + hw : hx - 1, hy, 1, 1, P.hat); else R(hx - 1, hy, hw + 2, 1, P.hat); }
  return hy;
}

/* ----- the cast (rank 0) and the others who stay: where they are, what they wear, their rank ----- */
const SAX = at(27.1, 29), CAFE_DOOR = at(27.3, 32.2), TBL1 = at(23.5, 33), TBL2 = at(22.6, 29.6), PHOTO = at(18.7, 32.4), DOORMAN = at(27.3, 45), LEANER = at(-1.3, 33), TBL3 = at(23.6, 41.5), TBL4 = at(24.3, 36.6), PROMP = at(-8, 26);
const hOf = q => Math.round(1.75 * q.s);
const P_SAX = look(hOf(SAX), 3, 0, '#2a2a3c', BOTS[1], { hat: '#3a3448', dk: 0.1 });
const P_WAITER = look(hOf(TBL1) + 1, 1, 0, '#f4f0ea', BOTS[1], { dk: 0.08 });
const P_HE = look(hOf(TBL1), 2, 0, TOPS[3], BOTS[2], { dk: 0.12 }), P_SHE = look(hOf(TBL1) - 1, 0, 4, TOPS[10], BOTS[3], { long: 2, skirt: '#ff6a8a', dk: 0.12 });
const P_F1 = look(hOf(TBL2), 0, 3, TOPS[1], BOTS[0], { long: 2, dk: 0.14 }), P_F2 = look(hOf(TBL2), 3, 0, TOPS[4], BOTS[3], { dk: 0.14 });
const P_BENCH = look(25, 1, 5, TOPS[11], BOTS[2], { glow: MOONL, hat: '#e8dcc0', dk: 0.3 });
const P_PHOTO = look(hOf(PHOTO), 0, 2, TOPS[6], BOTS[0], { long: 2, bag: '#7a4a6a', dk: 0.15 });
const P_DOOR = look(hOf(DOORMAN), 2, 0, TOPS[2], BOTS[1], { dk: 0.1 });
const P_LEAN = look(hOf(LEANER), 1, 1, TOPS[8], BOTS[4], { glow: MOONL, dk: 0.26 });
const P_T3A = look(hOf(TBL3), 1, 1, TOPS[9], BOTS[1], { dk: 0.1 }), P_T3B = look(hOf(TBL3), 0, 3, TOPS[0], BOTS[3], { long: 2, dk: 0.1 });
const P_SOLO = look(hOf(TBL4), 2, 0, TOPS[7], BOTS[4], { dk: 0.14 });
const P_PR1 = look(hOf(PROMP), 0, 2, TOPS[2], BOTS[0], { long: 2, glow: MOONL, dk: 0.28 }), P_PR2 = look(hOf(PROMP) + 1, 3, 0, TOPS[6], BOTS[2], { glow: MOONL, dk: 0.28 });
const RANK = { friendsDog: 0.2, photo: 0.3, pedicab: 0.35, table2: 0.4, cruiser: 0.45, table3: 0.55, jogger: 0.6, strollers: 0.65, promPair: 0.7, leaner: 0.75, skater: 0.8, solo: 0.85, door: 0.9 };
const SERVE = 40, SERVE_T0 = 10, CLINK = 48, CLINK_T0 = 22, PHRASE = 24, PHRASE_T0 = 6;
const NOTE = ['.#.', '.##', '.#.', '##.', '##.'];
function drawCast(t, crowd) {
  // the saxophonist under the cafe's arcade: he sways, and a phrase of notes rises every 24 s
  const ph = (((t - PHRASE_T0) % PHRASE) + PHRASE) % PHRASE, playing = ph < 9, sx = SAX.x + (playing ? Math.round(wave(t, 60, 0.1) * 1.2) : 0);
  put(SAX.y, () => {
    const hy = person(sx, SAX.y, P_SAX, { view: 2, dir: 1, aR: 4 }), by = hy + P_SAX.hh + 1;
    for (let k = 0; k < 7; k++) R(sx + 2 + (k > 4 ? 1 : 0), by + k, 1, 1, k % 3 ? '#e8b84a' : '#fff0a8');          // the saxophone
    R(sx + 3, by + 7, 2, 1, '#e8b84a'); R(sx + 5, by + 5, 1, 2, '#fff0a8');
    if (playing) for (let n = 0; n < 3; n++) {
      const u = frac(ph / 3 + n / 3), nx = sx + 7 + Math.round(u * 10 + Math.sin(u * 9 + n) * 2), ny = by + 3 - Math.round(u * 20);
      ctx.globalAlpha = Math.sin(Math.PI * u) * 0.9; ctx.fillStyle = n & 1 ? '#f6fffc' : '#8af4e4';
      NOTE.forEach((r5, j) => { for (let i = 0; i < 3; i++) if (r5[i] === '#') ctx.fillRect(nx + i, ny + j, 1, 1); });
      ctx.globalAlpha = 1;
    }
  });
  // the waiter: out to the couple's table with a tray, a word, and back to the door
  const w = (((t - SERVE_T0) % SERVE) + SERVE) % SERVE, ax = CAFE_DOOR.x + 4, bx = TBL1.x - 12;
  const wx = w < 7 ? lerp(ax, bx, smooth(0, 7, w)) : w < 12 ? bx : w < 19 ? lerp(bx, ax, smooth(12, 19, w)) : ax;
  const wy = lerp(CAFE_DOOR.y, TBL1.y + 1, (wx - ax) / (bx - ax)), walking = (w < 7 || (w >= 12 && w < 19)), tray = w < 9;
  put(wy, () => {
    const dir = w < 12 ? 1 : w < 19 ? -1 : 1;
    const hy = person(wx, wy, P_WAITER, walking ? { view: 2, dir, step: walkF(t), aR: tray ? 3 : 0 } : w < 12 ? { view: 2, dir: 1, aR: 3 } : { view: 1 });
    if (tray) { const tx = Math.round(wx) + 3, ty = hy + P_WAITER.hh + 2; R(tx, ty, 5, 1, '#d8dce8'); R(tx + 1, ty - 2, 1, 2, '#ffe8a0'); R(tx + 3, ty - 2, 1, 2, '#a8e8ff'); }
  });
  // the couple at their table: they talk, and clink glasses every 48 s
  const c = (((t - CLINK_T0) % CLINK) + CLINK) % CLINK, clink = c < 2.6, seat = TBL1.y - Math.round(0.45 * TBL1.s) + 1, X = Math.round(TBL1.x);
  put(TBL1.y, () => {
    const talk = hash(3, step(t, 96), 4);
    const hy = person(X - 7, seat, P_HE, { view: 2, dir: 1, sit: true, aR: clink ? 3 : talk < 0.3 ? 4 : 0 });
    person(X + 7, seat, P_SHE, { view: 2, dir: -1, sit: true, aR: clink ? 3 : talk > 0.75 ? 4 : 0, lean: talk > 0.5 && !clink ? -1 : 0 });
    const ty = Math.round(TBL1.y - 0.75 * TBL1.s);
    R(X - 4, ty, 9, 1, '#d8d0e4');                                                        // their table, in front of their knees
    if (clink) { R(X - 2, hy + P_HE.hh + 1, 1, 2, '#ffe8a0'); R(X + 1, hy + P_HE.hh + 1, 1, 2, '#ffe8a0'); if (c > 0.9 && c < 1.5) R(X, hy + P_HE.hh, 1, 1, '#ffffff'); }
    else { R(X - 3, ty - 2, 1, 2, '#ffe8a0'); R(X + 3, ty - 2, 1, 2, '#ffe8a0'); }
  });
  // the one on the bench under the palms, looking out to sea (a phone comes up when the ship passes)
  put(BENCH.y, () => {
    const film = t > 68 && t < 84, turn = !film && hash(5, step(t, 60), 6) < 0.2;
    person(BENCH.x - 1, BENCH.y - 7, P_BENCH, turn ? { view: 1, sit: true } : { view: 2, dir: 1, sit: true, aR: film ? 2 : 0 });
  });
  if (here(RANK.table2, crowd)) put(TBL2.y, () => {                                       // two friends at the next table
    const X2 = Math.round(TBL2.x), s2 = TBL2.y - Math.round(0.45 * TBL2.s) + 1, sip = frac(t / 16) < 0.14, scroll2 = frac(t / 30 + 0.4) < 0.5;
    person(X2 - 7, s2, P_F1, { view: 2, dir: 1, sit: true, aR: sip ? 6 : 0 });
    person(X2 + 8, s2, P_F2, { view: 2, dir: -1, sit: true, aR: scroll2 ? 2 : 0 });
    R(X2 - 4, Math.round(TBL2.y - 0.75 * TBL2.s), 10, 1, '#d8d0e4');
  });
  if (here(RANK.photo, crowd)) put(PHOTO.y, () => person(PHOTO.x, PHOTO.y, P_PHOTO, { view: 2, dir: 1, aR: frac(t / 20 + 0.3) < 0.6 ? 2 : 0 }));   // a picture of the convertible
  if (here(RANK.table3, crowd)) put(TBL3.y, () => {                                       // a couple on the hotel's terrace
    const X3 = Math.round(TBL3.x), s3 = TBL3.y - Math.round(0.45 * TBL3.s) + 1;
    person(X3 - 6, s3, P_T3A, { view: 2, dir: 1, sit: true, aR: frac(t / 22 + 0.2) < 0.12 ? 6 : 0 });
    person(X3 + 6, s3, P_T3B, { view: 2, dir: -1, sit: true, lean: frac(t / 34) < 0.4 ? -1 : 0 });
    R(X3 - 3, Math.round(TBL3.y - 0.75 * TBL3.s), 7, 1, '#d8d0e4');
  });
  if (here(RANK.solo, crowd)) put(TBL4.y, () => person(TBL4.x - 7, TBL4.y - Math.round(0.45 * TBL4.s) + 1, P_SOLO, { view: 2, dir: 1, sit: true, aR: frac(t / 28 + 0.5) < 0.7 ? 2 : 0 }));   // alone with a phone
  if (here(RANK.promPair, crowd)) put(PROMP.y, () => {                                    // two on the promenade, watching the sea
    const ship = t > SHIP.t0 + 6 && t < SHIP.t1 - 40, talk = hash(9, step(t, 80), 2);
    person(PROMP.x, PROMP.y, P_PR1, ship && frac(t / 12) < 0.3 ? { view: 2, dir: 1, aR: 3 } : talk < 0.3 ? { view: 2, dir: 1 } : { view: 0 });
    person(PROMP.x + 9, PROMP.y + 1, P_PR2, talk > 0.7 ? { view: 2, dir: -1 } : { view: 0, aR: frac(t / 40 + 0.3) < 0.2 ? 2 : 0 });
  });
  if (here(RANK.door, crowd)) put(DOORMAN.y, () => person(DOORMAN.x, DOORMAN.y, P_DOOR, { view: 1 }));
  if (here(RANK.leaner, crowd)) put(LEANER.y, () => person(LEANER.x, LEANER.y, P_LEAN, { view: 2, dir: -1, aR: frac(t / 26 + 0.1) < 0.3 ? 2 : 0 }));
}

/* ----- people who pass: up and down the street they grow and shrink; on the path they cross in profile ----- */
const L_FR1 = looks(0, 3, TOPS[5], BOTS[3], { long: 2, bag: '#5a6ab0' }), L_FR2 = looks(2, 0, TOPS[7], BOTS[0], { hat: '#e8dcc0' });
const L_ST1 = looks(1, 0, TOPS[2], BOTS[1]), L_ST2 = looks(0, 4, TOPS[8], BOTS[3], { long: 3, skirt: '#9a6ad8' });
const L_SK = looks(2, 1, TOPS[0], BOTS[4], { glow: MOONL }), L_CR = looks(1, 3, TOPS[9], BOTS[2], { long: 2, hat: '#f0e0b0', glow: MOONL });
const L_JOG = looks(3, 0, TOPS[1], BOTS[1], { glow: MOONL, dk: 0.3 });
const hAt = y => clamp(Math.round(1.75 * gs(y)), 4, 36);
function drawDog(x, y, dir, t) {                       // a small dog trotting on its lead
  const f = Math.floor(t * 6) & 1;
  R(x - 3, y - 4, 6, 3, '#d8a868'); R(dir > 0 ? x + 2 : x - 4, y - 6, 3, 3, '#d8a868'); R(dir > 0 ? x + 4 : x - 4, y - 7, 1, 1, '#8a5a34');
  R(dir > 0 ? x - 4 : x + 3, y - 5 - (f ? 1 : 0), 1, 2, '#f0d0a0');
  R(x - 3 + f, y - 1, 1, 2, '#8a5a34'); R(x + 1 - f, y - 1, 1, 2, '#8a5a34');
}
function drawBike(x, y, s, dir, t) {                   // a beach cruiser in profile: fat tyres, a swept frame, wide bars
  const r = Math.max(2, Math.round(0.34 * s)), wb = Math.round(1.05 * s), ax = x - dir * (wb >> 1), bx = x + dir * (wb >> 1), wy = y - r;
  for (const cx of [ax, bx]) for (let k = 0; k < 16; k++) { const a = k / 16 * TAU; R(Math.round(cx + Math.cos(a) * r), Math.round(wy + Math.sin(a) * r), 1, 1, k % 4 === Math.floor(t * 6) % 4 ? '#e8e4e0' : '#14121c'); }
  ctx.fillStyle = '#5cd0c4';
  for (let k = 0; k <= wb; k++) ctx.fillRect(Math.min(ax, bx) + k, wy - Math.round(Math.sin(k / wb * Math.PI) * r * 0.5), 1, 1);
  R(x - dir, wy - r - 1, 1, r + 1, '#5cd0c4'); R(bx, wy - r - 2, 1, r + 2, '#5cd0c4'); R(bx - dir, wy - r - 3, 3, 1, '#d8dce8'); R(x - dir * 2, wy - r - 2, 4, 1, '#3a2a2a');
}
function drawPassers(t, crowd) {
  // two friends and a small dog come up the path from the beach, cross the street and walk off down the pavement
  if (here(RANK.friendsDog, crowd) && t >= FRIENDS.t0 && t < FRIENDS.t1) {
    const u = (t - FRIENDS.t0) / (FRIENDS.t1 - FRIENDS.t0);
    let x, y;
    if (u < 0.76) { x = lerp(W + 22, 106, u / 0.76); y = PATH_Y; } else { const s = lerp(11.83, 18.6, (u - 0.76) / 0.24); x = VPX - 19 * s; y = HZ + EYE * s; }
    const h = hAt(y);
    put(y, () => {
      person(x, y, L_FR1[h], { view: 2, dir: -1, step: walkF(t) });
      person(x + Math.round(h * 0.45), y + 1, L_FR2[h - 1], { view: 2, dir: -1, step: walkF(t + 0.15), aR: hash(7, step(t, 60), 8) < 0.3 ? 3 : 0 });
      drawDog(Math.round(x) - Math.round(h * 0.5), Math.round(y) + 1, -1, t);
      ctx.fillStyle = '#b8b0c8'; for (let k = 1; k < Math.round(h * 0.4); k += 2) ctx.fillRect(Math.round(x) - k, Math.round(y) - Math.round(h * 0.45) + Math.round(k * 0.5), 1, 1);   // the lead
    });
  }
  // a couple strolls away up the hotels' pavement
  if (here(RANK.strollers, crowd) && t >= 148 && t < 226) {
    const d = lerp(18.4, 120, (t - 148) / 78), a = at(19.2, d), b = at(20.2, d), h = hAt(a.y);
    put(a.y, () => { person(b.x, b.y, L_ST1[h], { view: 0, step: walkF(t), aR: 0 }); person(a.x, a.y, L_ST2[Math.max(4, h - 1)], { view: 0, step: walkF(t + 0.16), lean: -1 }); });
  }
  // a skater rolls down the park-side pavement and turns along the path to the beach
  if (here(RANK.skater, crowd) && t >= SKATER.t0 && t < SKATER.t1 + 8) {
    const u = (t - SKATER.t0) / 20;
    if (u < 1) { const q = at(0.3 + 0.6 * Math.sin(u * 14), lerp(120, 27.9, u)), h = hAt(q.y); put(q.y, () => { person(q.x, q.y - 1, L_SK[h], { view: 1, aL: 3, aR: 3 }); R(Math.round(q.x) - 2, Math.round(q.y), 5, 1, '#e8d060'); }); }
    else { const x = lerp(gx(0.3, PATH_Y), W + 14, (u - 1) / 0.8), h = hAt(PATH_Y); put(PATH_Y, () => { person(x, PATH_Y - 1, L_SK[h], { view: 2, dir: 1, aR: 3 }); R(Math.round(x) - 4, PATH_Y, 9, 1, '#e8d060'); R(Math.round(x) - 3, PATH_Y + 1, 1, 1, '#d8dce8'); R(Math.round(x) + 3, PATH_Y + 1, 1, 1, '#d8dce8'); }); }
  }
  // someone on a beach cruiser comes up the path, rings, turns up the street and rides away
  if (here(RANK.cruiser, crowd) && t >= CRUISER.t0 && t < CRUISER.t1) {
    const xt = gx(3.4, PATH_Y);
    if (t < CRUISER.turn) {
      const x = lerp(W + 16, xt, (t - CRUISER.t0) / (CRUISER.turn - CRUISER.t0)), s = gs(PATH_Y), h = hAt(PATH_Y), sy = PATH_Y - Math.round(0.95 * s);
      put(PATH_Y, () => { drawBike(Math.round(x), PATH_Y, s, -1, t); person(x + 1, sy, L_CR[h], { view: 2, dir: -1, sit: true, aR: 3 }); });
    } else {
      const d = lerp(27.9, 135, Math.pow((t - CRUISER.turn) / (CRUISER.t1 - CRUISER.turn), 1.2)), q = at(3.4, d), h = hAt(q.y), r = Math.max(1, Math.round(0.34 * q.s));
      put(q.y, () => {
        R(Math.round(q.x), Math.round(q.y) - 2 * r, 1, 2 * r + 1, '#14121c'); if (q.s > 4) R(Math.round(q.x), Math.round(q.y) - r - 1, 1, 1, '#ff4a4a');
        person(q.x, q.y - Math.round(0.55 * q.s), L_CR[h], { view: 0, step: walkF(t * 1.5) });
        if (q.s > 5) R(Math.round(q.x) - Math.round(0.3 * q.s) - 1, Math.round(q.y - 1.2 * q.s), Math.round(0.6 * q.s) + 3, 1, '#d8dce8');
      });
    }
  }
  // a jogger comes down the promenade and out of the frame on the right
  if (here(RANK.jogger, crowd) && t >= 30 && t < 96) {
    const q = at(-8, lerp(140, 15.5, Math.pow((t - 30) / 66, 0.9))), h = hAt(q.y);
    put(q.y, () => person(q.x, q.y, L_JOG[h], { view: 1, step: Math.floor(t * 4.8) & 1 }));
  }
}

/* ----- what drives: a low car each way, slow, and a pedicab under its lights ----- */
function drawCar(q, t, away, body, dark) {             // seen from behind (away) or head on, w px wide at scale q.s
  const s = q.s, x = Math.round(q.x), y = Math.round(q.y), w = Math.max(3, Math.round(1.85 * s)), x0 = x - (w >> 1);
  const bh = Math.max(2, Math.round(0.5 * s)), ch = Math.max(1, Math.round(0.42 * s)), by = y - Math.round(0.2 * s) - bh, cw = Math.max(2, Math.round(w * 0.72)), cx = x - (cw >> 1);
  const wl = Math.max(1, Math.round(0.24 * s));
  R(x0, y - Math.max(1, Math.round(0.3 * s)), wl, Math.max(1, Math.round(0.3 * s)) + 1, '#08080f'); R(x0 + w - wl, y - Math.max(1, Math.round(0.3 * s)), wl, Math.max(1, Math.round(0.3 * s)) + 1, '#08080f');
  R(x0, by, w, bh, body); R(x0, by, w, 1, mixHex(body, '#ffffff', 0.35)); R(x0, by + bh - 1, w, 1, '#c0c4d4');
  R(cx, by - ch, cw, ch, dark); if (ch > 2) R(cx + 1, by - ch + 1, cw - 2, ch - 1, away ? '#26305a' : '#3a4a7a'); R(cx, by - ch, cw, 1, mixHex(body, '#ffffff', 0.2));
  const lw = Math.max(1, Math.round(0.3 * s)), lh = Math.max(1, Math.round(0.14 * s)), ly = by + Math.max(0, Math.round(0.08 * s));
  for (const lx of [x0, x0 + w - lw]) R(lx, ly, lw, lh, away ? '#ff3a4a' : '#fff6d8');
  if (s > 6 && away) R(x - 2, by + bh - 3, 5, 2, '#f0e8c0');
  const I = clamp(s / 9, 0.25, 1);
  for (const lx of [x0 + (lw >> 1), x0 + w - 1 - (lw >> 1)]) {
    halo(away ? HL.tail : HL.head, lx, ly, I * (away ? 0.8 : 0.55));
    if (y < H - 4) streak(ctx, t, lx, Math.max(2, lw + 1), away ? '#ff4a5a' : '#fff0c8', away ? 0.45 : 0.6, lx, y + 1, Math.min(H, y + Math.round(5 * s)));
  }
  if (away) halo(HL.under, x, y, I * 0.9);                                              // a violet glow under the low car
}
function drawPedicab(q, t) {                           // from behind: a bench under a canopy edged with lights, two heads, the rider ahead
  const s = q.s, x = Math.round(q.x), y = Math.round(q.y), w = Math.max(3, Math.round(1.25 * s)), x0 = x - (w >> 1), h = Math.round(1.9 * s);
  const col = ['#4ff0e0', '#ff5cb0', '#b890ff', '#ffd84a'][step(t, 60) % 4];
  R(x0, y - Math.round(0.6 * s), 1, Math.round(0.6 * s) + 1, '#14121c'); R(x0 + w - 1, y - Math.round(0.6 * s), 1, Math.round(0.6 * s) + 1, '#14121c');
  R(x0, y - Math.round(1.05 * s), w, Math.round(0.75 * s), '#2a2448'); R(x0, y - Math.round(1.05 * s), w, 1, '#5a4a8a');
  if (s > 5) { const hs = Math.max(2, Math.round(0.24 * s)); R(x0 + 1, y - Math.round(1.05 * s) - hs, hs, hs, '#3c2418'); R(x0 + w - 1 - hs, y - Math.round(1.05 * s) - hs, hs, hs, '#d0a858'); }
  R(x0, y - h, w, 1, col); R(x0, y - h, 1, Math.round(0.3 * s) + 1, col); R(x0 + w - 1, y - h, 1, Math.round(0.3 * s) + 1, col);
  if (s > 4) R(x0 + 1, y - h + 1, w - 2, Math.max(1, Math.round(0.14 * s)), '#3a3068');
  R(x, y - Math.round(0.35 * s), 1, 1, '#ff4a4a');
  halo(HL.neon[col], x, y - h, clamp(s / 10, 0.3, 0.9));
  if (y < H - 4) streak(ctx, t, x, Math.max(2, w >> 1), col, 0.4, 7, y + 1, Math.min(H, y + Math.round(4 * s)));
}
function drawTraffic(t, crowd) {
  if (t >= CAR_AWAY.t0 && t < CAR_AWAY.t1) { const q = at(7.2, lerp(12.5, 150, Math.pow((t - CAR_AWAY.t0) / (CAR_AWAY.t1 - CAR_AWAY.t0), 1.25))); put(q.y, () => drawCar(q, t, true, '#7a3ab0', '#1c1438')); }
  if (t >= CAR_IN.t0 && t < CAR_IN.t1) { const q = at(12.3, lerp(150, 12.5, Math.pow((t - CAR_IN.t0) / (CAR_IN.t1 - CAR_IN.t0), 0.8))); put(q.y, () => drawCar(q, t, false, '#b0483a', '#241a2c')); }
  if (here(RANK.pedicab, crowd) && t >= PEDICAB.t0 && t < PEDICAB.t1) { const q = at(5.8, lerp(13, 150, Math.pow((t - PEDICAB.t0) / (PEDICAB.t1 - PEDICAB.t0), 1.15))); put(q.y, () => drawPedicab(q, t)); }
}

/* ================= Animals: ibises on the lawn, parakeets, a pelican, gulls, a gecko, moths ================= */
const IBIS = { up: ['....WW.', '...WWP.', '..WW.P.', 'WWWW..P', 'WWWW...', '.L.L...', '.L.L...'], peck: ['.......', '.......', 'WWWWW..', 'WWWWWW.', '.L.LWP.', '.L.L.P.', '.L.L..P'] };
const IBIS_PAL = { W: '#e8ecf4', P: '#f08a8a', L: '#d87878' };
function drawBirds(t) {
  // two white ibises working the wet lawn
  [[414, 249, 24, 2, 0.1], [398, 262, 14, 3, 0.6], [436, 238, 10, 3, 0.35]].forEach(([cx, y, ax, k, ph], i) => {
    const x = cx + ax * wave(t, k, ph), vel = Math.cos(TAU * (k * t / LOOP + ph)), still = Math.abs(vel) < 0.35;
    const fr = still && hash(i, step(t, 320), 3) < 0.6 ? 1 : 0;
    ctx.drawImage(IBIS_C[fr][vel < 0 ? 1 : 0], Math.round(x) - 3, y - 7 + (!still && (step(t, 960) & 1) ? 1 : 0));
  });
  // wild parakeets cross from the palms to the hotel roofs, green and quick
  if (t >= PARAKEETS.t0 && t < PARAKEETS.t1) {
    const u = (t - PARAKEETS.t0) / (PARAKEETS.t1 - PARAKEETS.t0);
    for (let k = 0; k < 7; k++) {
      const uu = clamp(u * 1.25 - k * 0.035, 0, 1), x = lerp(402, -12, uu) + Math.sin(k * 2.1) * 8 * uu, y = 66 - Math.sin(uu * Math.PI) * 16 + k * 3 * uu + Math.sin(uu * 14 + k) * 3;
      if (uu <= 0 || uu >= 1) continue;
      const f = Math.floor(t * 10 + k) & 1;
      R(Math.round(x), Math.round(y), 3, 1, '#58c84a'); R(Math.round(x) + 1, Math.round(y) + (f ? -1 : 1), 1, 1, '#9af07a'); R(Math.round(x) + 3, Math.round(y), 1, 1, '#2a7a3a');
    }
  }
  // a brown pelican glides in low over the surf
  if (t >= PELICAN.t0 && t < PELICAN.t1) {
    const u = (t - PELICAN.t0) / (PELICAN.t1 - PELICAN.t0), x = Math.round(lerp(W + 10, 338, u)), y = Math.round(128 + 6 * Math.sin(u * 5) + u * 4), f = frac(t / 3) < 0.25 ? Math.floor(t * 5) & 1 : 2;
    ctx.globalAlpha = Math.min(1, (1 - u) * 8);
    R(x, y, 6, 2, '#5a4a52'); R(x - 3, y - 1, 3, 2, '#d8cfc0'); R(x - 6, y, 3, 1, '#e8b860');
    if (f === 2) R(x - 2, y - 1, 9, 1, '#3e3440'); else { R(x + 1, y + (f ? 2 : -3), 4, 2, '#3e3440'); R(x + 3, y + (f ? 3 : -4), 3, 1, '#3e3440'); }
    ctx.globalAlpha = 1;
  }
  // two gulls drift over the beach while the ship is out
  if (t >= 146 && t < 172) for (let k = 0; k < 2; k++) {
    const u = (t - 146) / 26, x = Math.round(lerp(352, W + 8, u) + k * 14), y = Math.round(118 - 10 * Math.sin(u * 4 + k) - k * 5), f = Math.floor(t * 4 + k) & 1;
    ctx.globalAlpha = Math.min(1, u * 8);
    R(x, y, 2, 1, '#e8ecf4'); R(x - 2, y + (f ? -1 : 0), 2, 1, '#c8ccd8'); R(x + 2, y + (f ? -1 : 0), 2, 1, '#c8ccd8');
    ctx.globalAlpha = 1;
  }
}
const CAT = ['.#.#......', '.####.....', '######....', '#########.', '##########'];
function drawSmallLife(t, lamp) {
  // one of South Beach's cats on the coral-rock wall: its tail swings, it blinks
  const cx = Math.round(gx(WALL_L - 0.25, 216)) - 5, cy = 216 - 7;
  ctx.fillStyle = '#17142a';
  CAT.forEach((r10, j) => { for (let i = 0; i < r10.length; i++) if (r10[i] === '#') ctx.fillRect(cx + i, cy + j, 1, 1); });
  const ts = wave(t, 30, 0.2) * 0.9 + wave(t, 97, 0.5) * 0.4;
  for (let j = 0; j < 5; j++) ctx.fillRect(cx + 10 + Math.round(ts * j / 4), cy + 4 + j, 1, 1);
  if (!(frac(t / 4) > 0.955 || frac(t / 30) > 0.985)) R(cx + 1, cy + 2, 1, 1, '#d6f060');
  // a house gecko on the cafe's wall, under the tube: every 30 s it darts at a moth, then creeps back
  const g = (((t - 4) % 30) + 30) % 30, gd = g < 0.3 ? g / 0.3 : g < 3.5 ? 1 : g < 9 ? 1 - (g - 3.5) / 5.5 : 0, gxp = Math.round(60 - gd * 7), gyp = 133 - Math.round(gd * 2);
  R(gxp, gyp, 4, 1, '#d8cfa8'); R(gxp - 1, gyp, 1, 1, '#f0e8c8'); R(gxp + 4, gyp + (Math.floor(t * 2) & 1), 2, 1, '#b0a888'); R(gxp + 1, gyp - 1, 1, 1, '#b0a888'); R(gxp + 3, gyp + 1, 1, 1, '#b0a888');
  // moths round the cafe's tube and the near lamp
  ctx.fillStyle = '#fff2e0';
  for (let k = 0; k < 2; k++) { const a = TAU * (31 + k * 9) * t / LOOP + k * 2; ctx.fillRect(Math.round(46 + Math.cos(a) * (5 + k * 2)), Math.round(126 + Math.sin(1.7 * a) * 3), 1, 1); }
  if (lamp > 0.3) for (let k = 0; k < 4; k++) { const a = TAU * (37 + k * 11) * t / LOOP + k * 1.7; ctx.fillRect(Math.round(LAMPS[0].hx + Math.cos(a) * (4 + k * 1.5)), Math.round(LAMPS[0].hy + 3 + Math.sin(2 * a + k) * 3), 1, 1); }
}
/* the last drops off the fronds, the awning and the marquee (the shower is over; they fall in every weather) */
const DRIPS = [];
for (let k = 0; k < 6; k++) { const x = 8 + k * 12; DRIPS.push({ x, y0: Math.round(baseY(x) - 3.6 * S(x)), y1: Math.round(baseY(x)) + 3, n: 34 + k * 5, ph: k * 0.23 }); }
for (let k = 0; k < 4; k++) { const x = 92 + k * 15; DRIPS.push({ x, y0: Math.round(baseY(x) - 3.55 * S(x)) + 1, y1: Math.round(baseY(x)) + 2, n: 30 + k * 7, ph: k * 0.31 + 0.1 }); }
[[348, 108, 199], [374, 104, 201], [380, 86, 214], [414, 84, 216], [344, 160, 190], [398, 160, 193], [224, 124, 176]].forEach(([x, y0, y1], k) => DRIPS.push({ x, y0, y1, n: 26 + k * 6, ph: k * 0.17 + 0.05 }));
function drawDrips(t) {
  const g = 260;
  ctx.fillStyle = '#c0c8ff';
  for (const d of DRIPS) {
    const T = LOOP / d.n, q = t / T + d.ph, u = q - Math.floor(q), tau = u * T, rel = T * 0.6;
    if (tau < rel) { ctx.globalAlpha = 0.25 + 0.6 * tau / rel; ctx.fillRect(d.x, d.y0, 1, 1); continue; }
    const s = tau - rel, y = d.y0 + 0.5 * g * s * s;
    if (y < d.y1 - 1) { ctx.globalAlpha = 0.85; ctx.fillRect(d.x, Math.round(y), 1, 2); continue; }
    const ss = (s - Math.sqrt(2 * (d.y1 - 1 - d.y0) / g)) / 0.35;
    if (ss >= 1) continue;
    const hy = d.y1 - 1 - Math.round(Math.sin(Math.PI * ss) * 2), sp = 1 + Math.round(ss * 2);
    ctx.globalAlpha = 1 - ss;
    ctx.fillRect(d.x - sp, hy, 1, 1); ctx.fillRect(d.x + sp, hy, 1, 1);
  }
  ctx.globalAlpha = 1;
}

/* ================= Sea haze over the far end of the street and the beach (drifts, breathes) ================= */
const HAZE = { y: 104, h: 70, levels: 8 };
function makeHaze() {
  const { h, levels } = HAZE, F = new Float32Array(W * h), out = [], A = hex('#7c6c9c'), B = hex('#b08aa4');
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = y / (h - 1), prof = Math.pow(Math.sin(Math.PI * Math.pow(v, 0.9)), 1.5);
    F[y * W + x] = prof * clamp(0.5 + 0.32 * Math.sin(TAU * 2 * x / W + y * 0.06) + 0.16 * Math.sin(TAU * 5 * x / W - y * 0.2) + 0.1 * Math.sin(TAU * 11 * x / W + y * 0.4), 0, 1);
  }
  for (let k = 0; k <= levels; k++) {
    const p = pix(W, h), D = k / levels * 1.2;
    for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
      const q = Math.floor(Math.min(1, F[y * W + x] * D) * 4 + bayer(x, y)) / 4;
      if (q > 0) p.set(x, y, [lerp(A[0], B[0], y / h), lerp(A[1], B[1], y / h), lerp(A[2], B[2], y / h)], Math.round(Math.min(1, q) * 255));
    }
    out.push(p.done());
  }
  return out;
}
function drawHaze(t) {
  const D = clamp(0.55 + 0.25 * wave(t, 3, 0.1) + 0.08 * wave(t, 7, 0.6), 0, 0.999) * HAZE.levels, k = Math.floor(D), fr = D - k, off = scroll(t, 1, W);
  for (const [cv, a] of [[HAZE_C[k], 1 - fr], [HAZE_C[k + 1], fr]]) {
    if (a < 0.02) continue;
    ctx.globalAlpha = a * 0.34;
    ctx.drawImage(cv, off - W, HAZE.y); ctx.drawImage(cv, off, HAZE.y);
  }
  ctx.globalAlpha = 1;
}

/* ================= Royal poinciana blossoms on the sea breeze (three depths; the breeze blows inland, to the left) ================= */
const PETAL_SHAPES = [null, ['##.', '.##'], ['.##..', '####.', '#####', '.##.#']];
function makePetal(size, tone) {
  const g = PETAL_SHAPES[size], p = pix(g[0].length, g.length), pal = [['#ff6a34', '#e23a20', '#a82418'], ['#ff8a44', '#f0502a', '#c03020']][tone];
  g.forEach((r5, j) => { for (let i = 0; i < r5.length; i++) if (r5[i] === '#') p.set(i, j, j === 0 ? pal[0] : j === g.length - 1 ? pal[2] : pal[1]); });
  if (size === 2) p.set(2, 1, '#ffe8a0');               // the pale, streaked standard petal
  return p.done();
}
const PETALS = [[], [], []];
{
  const r = rng(909);
  [{ count: 30, nMin: 7, nMax: 10, amp: 3, drift: [80, 160], fall: [150, 200], tree: 0.3 },
    { count: 22, nMin: 9, nMax: 12, amp: 5, drift: [170, 300], fall: [190, 250], tree: 0.4 },
    { count: 12, nMin: 11, nMax: 14, amp: 8, drift: [260, 420], fall: [250, 300], tree: 0.25 }].forEach((d, size) => {
    for (let i = 0; i < d.count; i++) PETALS[size].push({
      id: size * 100 + i, size, n: d.nMin + Math.floor(r() * (d.nMax - d.nMin + 1)), ph: r(), ks: 40 + Math.floor(r() * 40), p2: r(),
      kr: 20 + Math.floor(r() * 30), p3: r(), kf: 30 + Math.floor(r() * 50), p4: r(), amp: d.amp * (0.7 + r() * 0.6),
      drift: lerp(d.drift[0], d.drift[1], r()), fall: lerp(d.fall[0], d.fall[1], r()), tree: d.tree, tone: r() < 0.5 ? 0 : 1,
    });
  });
}
function drawPetals(list, t) {
  for (const pt of list) {
    const { cyc, u } = cycle(t, pt.n, pt.ph);
    let x0, y0, fall = pt.fall;
    if (hash(pt.id, cyc, 3) < pt.tree) { x0 = POIN.x - 28 + hash(pt.id, cyc, 1) * 56; y0 = 142 + hash(pt.id, cyc, 2) * 10; fall = (pt.size + 1) * 30; }
    else { x0 = 60 + hash(pt.id, cyc, 1) * (W + 200); y0 = -8 - hash(pt.id, cyc, 2) * 20; }
    const sw = wave(t, pt.ks, pt.p2), x = x0 - u * pt.drift + sw * pt.amp, y = y0 + u * fall + (1 - Math.abs(sw)) * (pt.size + 1) * 0.8;
    if (x < -8 || x > W + 8 || y > H + 4) continue;
    const a = Math.min(1, u / 0.06, (1 - u) / 0.1), fl = Math.cos(TAU * (pt.kf * t / LOOP + pt.p4));
    if (pt.size === 0) { ctx.globalAlpha = a * 0.9; ctx.fillStyle = fl < 0 ? '#ff8a44' : '#e84428'; ctx.fillRect(Math.round(x), Math.round(y), Math.abs(fl) > 0.45 ? 2 : 1, 1); continue; }
    const spr = PETAL_SPR[pt.size][fl < 0 ? 1 - pt.tone : pt.tone];
    ctx.save();
    ctx.globalAlpha = a;
    ctx.translate(Math.round(x), Math.round(y)); ctx.rotate(sw * 0.9 + 0.5 * wave(t, pt.kr, pt.p3)); ctx.scale(1, Math.max(0.3, Math.abs(fl)));
    ctx.drawImage(spr, -(spr.width >> 1), -(spr.height >> 1));
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}

/* ================= The billboard: a lit poster on the tall hotel's blank south wall (CAFECITO, a made-up coffee window) ================= */
const BB_TEAL = ['#0e4a58', '#16707a', '#22969a', '#3cbcb4', '#7adcc8'];
const CUP = ['.WWWWWWW..', 'WKKKKKKKW.', 'WWWWWWWWWW', '.WWWWWWW.W', '.WWWWWWWW.', '..WWWWW...', 'SSSSSSSSS.'];
function renderBBFrame() {
  const p = pix(W, H), x0 = BB.x - 2, x1 = BB.x + BB.w + 1, y0 = BB.y - 2, y1 = BB.y + BB.h + 1;
  p.fill(x0, y0, x1 - x0 + 1, y1 - y0 + 1, '#1a1634'); p.fill(x0, y0, x1 - x0 + 1, 1, '#5a4e86'); p.fill(x0, y1, x1 - x0 + 1, 1, '#0e0c22');
  p.fill(x0 + 1, y0 + 1, x1 - x0 - 1, 1, '#e87ab0'); p.fill(x0 + 1, y1 - 1, x1 - x0 - 1, 1, '#8a3a6a');
  for (const lx of BB_LAMPS) {                         // gooseneck lamps over the top
    p.fill(lx, BB.y - 6, 1, 4, '#3a3458'); p.fill(lx - 1, BB.y - 7, 3, 1, '#3a3458'); p.fill(lx - 2, BB.y - 6, 5, 1, '#241e3c'); p.fill(lx - 1, BB.y - 5, 3, 1, '#fff4d4');
  }
  return trim(p);
}
function renderAd(dim) {                               // dim: the right-hand lamp is out
  const p = pix(BB.w, BB.h);
  for (let j = 0; j < BB.h; j++) for (let i = 0; i < BB.w; i++) {
    let cone = 0;
    BB_LAMPS.forEach((lx, k) => { const dx = (i - (lx - BB.x)) / (5 + j * 0.8); cone = Math.max(cone, (k === 2 && dim ? 0.2 : 1) * Math.exp(-dx * dx) * Math.max(0, 1 - j / 26)); });
    const ray = Math.floor(Math.atan2(j - 13, i - 6) / TAU * 22 + 40) & 1;          // a deco sunburst behind the cup
    p.set(i, j, pick(BB_TEAL, 0.2 + cone * 0.55 + (ray ? 0.16 : 0), i, j));
  }
  CUP.forEach((r7, j) => { for (let i = 0; i < r7.length; i++) if (r7[i] !== '.') p.set(1 + i, 9 + j, r7[i] === 'W' ? (i < 3 ? '#ffffff' : '#f0e4d8') : r7[i] === 'K' ? '#4a2416' : '#f6c8dc'); });
  textP(p, 'CAFECITO', 12, 4, '#5a1030'); textP(p, 'CAFECITO', 11, 3, '#fff2d8');
  textP(p, '24 HORAS', 12, 12, '#0c3440'); textP(p, '24 HORAS', 11, 11, '#ffb0d4');
  return p.done();
}
function drawBillboard(t, pitch) {
  const lv = flick(t, BB_FLICK, 13);
  halo(HL.bbGlow, BB.x + (BB.w >> 1), BB.y + 4, (0.9 + 0.1 * wave(t, 8, 0.2)) * (2 + lv) / 3);
  blit(BBF_C);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(AD_C[0], BB.x, BB.y);
    if (lv < 1) { ctx.globalAlpha = 1 - lv; ctx.drawImage(AD_C[1], BB.x, BB.y); ctx.globalAlpha = 1; }
    for (const [ox, ph] of [[4, 0], [7, 0.45]]) for (let j = 1; j <= 6; j++) {       // steam off the little cup
      const sway = Math.sin(j * 1.1 + TAU * (72 * t / LOOP + ph)) * Math.min(1.6, j * 0.45);
      R(BB.x + 1 + ox + Math.round(sway), BB.y + 9 - j, 1, 1, j < 3 ? '#e8f8f4' : j < 5 ? '#b8e4dc' : '#7accc0');
    }
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, i) => {
    const a = i === 2 ? lv : 1;
    if (a < 1) R(lx - 1, BB.y - 5, 3, 1, `rgba(40,26,40,${(0.85 * (1 - a)).toFixed(3)})`);
    halo(HL.bbLamp, lx, BB.y - 4, a * (0.92 + 0.08 * wave(t, 700 + i * 37)));
  });
}

/* ================= The picked weather (site/js/engine/weather.js) ================= */
// Miami's own falling thing is the poinciana's blossoms (the city's own weather only). Rain, snow and leaves land far
// up the street, on the street and past the bottom of the frame, and catch the neon and the lamps.
const WEATHER = weatherPlans(['poinciana']);
const WX_LAND = [[150, 196], [200, 252], [H + 4, H + 4]];
const WX_LIGHTS = [{ x: FIN_X, y: 96, rx: 30, ry: 50, col: '#ff8ac8' }, { x: 36, y: 130, rx: 30, ry: 22, col: '#ffe070' }, { x: LAMP_E.x - 8, y: 150, rx: 26, ry: 40 },
  { x: 176, y: 150, rx: 24, ry: 18, col: '#8dffc0' }, { x: 100, y: 180, rx: 44, ry: 16, col: '#ffc888' }];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y + 6, rx: 30, ry: 18, col: '#ffe8c8' }] };

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, CLOUDS, STORM_C, FAR, SEA_C, SHIP_C, GROUND_C, POIN_C, TOWER_C, TERRACE_C, PARKED, BENCH_C, IBIS_C, HAZE_C,
  PETAL_SPR, BBF_C, AD_C, PITCH, VIG_C, POOL;
function renderVignette() {
  const p = pix(W, H), c = [6, 4, 22];
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.74, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(q * 0.42 * 255));
  }
  return p.done();
}
function prerender() {
  HL = {
    moon: makeHalo(40, '#a8b4e8', 0.2, 1.8), moonCore: makeHalo(13, '#f4f0dc', 0.3, 1.5), heat: makeHalo(64, '#c4a4f0', 0.2, 1.5),
    neon: { [AQUA]: makeHalo(9, AQUA, 0.3), [PINK]: makeHalo(10, PINK, 0.32), [YEL]: makeHalo(10, YEL, 0.3), [LILAC]: makeHalo(9, LILAC, 0.34), [MINT]: makeHalo(9, MINT, 0.28) },
    finial: makeHalo(5, '#ffb0d8', 0.6, 1), finGlow: makeHalo(52, '#ff6ab8', 0.16, 1.6), ship: makeHalo(26, '#ffd898', 0.12), uplight: makeHalo(13, '#ffe6b0', 0.3), poin: makeHalo(30, '#ff7a40', 0.1),
    bulb: makeHalo(4, '#ffd89a', 0.35, 1), candle: makeHalo(7, '#ffc870', 0.38), lamp: makeHalo(46, '#ffd8a0', 0.2, 1.7), lampCore: makeHalo(12, '#fff4d8', 0.45, 1.4),
    lampFar: makeHalo(8, '#ffe0b0', 0.4), head: makeHalo(11, '#fff4d0', 0.5), tail: makeHalo(8, '#ff3a4a', 0.5), under: makeHalo(18, '#a050ff', 0.25),
    bbGlow: makeHalo(34, '#ffdcb0', 0.14, 1.6), bbLamp: makeHalo(6, '#ffe8c8', 0.5, 1.3), door: makeHalo(20, '#ffc070', 0.2),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer); STORM_C = renderStorm(); FAR = renderFar();
  SEA_C = renderSea(); SHIP_C = renderShip();
  renderHotels();
  GROUND_C = renderGround();
  for (const pm of PALMS) { pm.trunk = renderTrunk(pm); pm.crowns = []; for (let f = 0; f < PALM_NF; f++) pm.crowns.push(renderCrown(pm, f / (PALM_NF - 1) * 2 - 1)); }
  POIN_C = renderPoinciana(); TOWER_C = renderTower(); TERRACE_C = renderTerrace(); PARKED = renderParked(); BENCH_C = renderBench();
  for (const l of LAMPS) l.c = renderLamp(l);
  IBIS_C = ['up', 'peck'].map(k => [sprite(IBIS[k], IBIS_PAL, false), sprite(IBIS[k], IBIS_PAL, true)]);
  HAZE_C = makeHaze();
  PETAL_SPR = [null, 1, 2].map(s => s && [makePetal(s, 0), makePetal(s, 1)]);
  BBF_C = renderBBFrame(); AD_C = [renderAd(false), renderAd(true)]; PITCH = makePitch(BB.w, BB.h, { accent: '#dd88bb' });
  VIG_C = renderVignette(); POOL = makePool(22, 6, '#ffd8a0', 0.26);
  [SNAP, SNAPX] = makeCanvas(VPX, SNAP_H); [MIR, MIRX] = makeCanvas(VPX, H - HZ); [SOFT, SOFTX] = makeCanvas(VPX, H - HZ);
}

/* ================= Frame compositor (back to front) ================= */
function drawScroll(cv, y, k, t) { const off = scroll(t, k, W); ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y); }
function drawSky(t, heat) {
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e6e6ff';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.85 + 0.15 * wave(t, 4)); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - 8, MOON.y - 8);
  if (heat) halo(HL.heat, STORM.x + HEAT_SPOTS[heat.k][0], STORM.y + HEAT_SPOTS[heat.k][1], heat.I);
  drawScroll(CLOUDS[0].c, CLOUDS[0].y, CLOUDS[0].k, t);
  drawScroll(CLOUDS[1].c, CLOUDS[1].y, CLOUDS[1].k, t);
  ctx.drawImage(STORM_C.c, STORM.x, STORM.y);
  if (heat) { ctx.globalAlpha = heat.I; ctx.drawImage(STORM_C.lit[heat.k], STORM.x, STORM.y); ctx.globalAlpha = 1; }
  drawScroll(CLOUDS[2].c, CLOUDS[2].y, CLOUDS[2].k, t);
  blit(FAR.c);
  for (const w of FAR.wins) if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p) R(w.x, w.y, 1, 1, w.col);
}
/** render(t) paints the city's own ad on the billboard; { pitch } paints the site's face on it; { billboard: false }
 *  leaves the poster and its lamps off the wall (the loop videos); { weather } draws a picked weather (the city's own
 *  when left out); { crowd } the share of the people to show (CROWD when left out; 0 = nobody) */
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  const lamp = (0.95 + 0.05 * wave(t, 1500, 0.3)) * flick(t, LAMP_FLICK, 11);
  drawSky(t, wx.poinciana && !reducedMotion ? heatAt(t) : null);
  blit(SEA_C); drawShip(t);
  if (wx.poinciana) drawPetals(PETALS[0], t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawHotels(t);
  if (billboard) drawBillboard(t, pitch);
  drawHaze(t);
  snapMirror();
  blit(GROUND_C);
  layMirror(t);
  drawSea(t);
  drawStreetFx(t, lamp);
  drawPool(POOL, LAMP_E.x + 3, LAMP_E.y + 6, lamp * 0.6);
  drawTerrace(t);
  drawOats(t);
  if (wx.poinciana) drawPetals(PETALS[1], t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  DL = [];
  for (const pm of PALMS) put(pm.y, () => drawPalm(pm, t));
  put(POIN.y, () => drawPoinciana(t)); put(TOWER.y, () => drawTower(t)); put(BENCH.y - 0.5, () => blit(BENCH_C));
  for (const car of PARKED) put(car.row, () => blit(car));
  LAMPS.forEach((l, i) => put(l.y, () => {
    blit(l.c);
    if (l.near) { halo(HL.lamp, l.hx, l.hy + 2, lamp); halo(HL.lampCore, l.hx, l.hy + 1, lamp); } else halo(HL.lampFar, l.hx, l.hy, 0.85 + 0.15 * wave(t, 600 + i * 31));
  }));
  for (const bx of BOLLARDS) put(PATH_Y - 6, () => halo(HL.bulb, bx, PATH_Y - 9, 0.9));
  drawTraffic(t, crowd);
  if (crowd > 0) { drawCast(t, crowd); drawPassers(t, crowd); }
  DL.sort((a, b) => a.y - b.y);
  for (const o of DL) o.f();
  drawBirds(t);
  drawSmallLife(t, lamp);
  drawDrips(t);
  if (wx.poinciana) drawPetals(PETALS[2], t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'miami',
  name: 'Miami',
  tagline: 'Neon after rain on Ocean Drive',
  timeZone: 'America/New_York',
  accent: '#dd88bb',                                   // Art Deco pastel pink, written out: tools/seo/seo.mjs reads it from the source
  focusX: FIN_X,
  landmark: [78, HERO.x1],                             // the hero hotel: its rounded corner, its front and the neon fin
  landmarkTop: MAST_TOP,                               // the row of the mast's tip
  billboard: BB,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  ambience: { waves: 0.4, wind: 0.2, crickets: 0.3, traffic: 0.12, crowd: 0.12 },
  events: [
    { t: 17, type: 'thunder', x: 450, gain: 0.14 }, { t: CLINK_T0 + 1, type: 'chime', x: 96, gain: 0.2 },
    { t: SHIP.horn, type: 'horn', x: 470, gain: 0.35 }, { t: 106, type: 'thunder', x: 450, gain: 0.12 },
    { t: CLINK_T0 + 1 + 2 * CLINK, type: 'chime', x: 96, gain: 0.2 }, { t: 148, type: 'gull', x: 400, gain: 0.4 }, { t: 200, type: 'thunder', x: 440, gain: 0.14 },
    { t: CRUISER.bell, type: 'chime', x: 340, gain: 0.4 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
